import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth, API, useRecording } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { 
  Swords, Users, Vote, Send, Crown, ArrowLeft, Clock, Lock,
  Video, Mic, MessageSquare, Play, Square, Bookmark, Camera,
  CheckCircle, AlertTriangle, XCircle, Film, X, Smartphone, Info, Maximize2
} from "lucide-react";
import Navbar from "@/components/Navbar";
import TugOfWarVoting from "@/components/TugOfWarVoting";
import DebatePlayback from "@/components/DebatePlayback";

// Branded Round Names
const ROUND_NAMES = {
  1: { name: "Hot Take", display: "🔥 Hot Take", description: "Opening statement", action: "Start Hot Take" },
  2: { name: "The Clapback", display: "👏 The Clapback", description: "Rebuttal round", action: "Start Clapback" },
  3: { name: "The Mic Drop", display: "🎤 The Mic Drop", description: "Closing statement", action: "Start Mic Drop" }
};

// Available emotes (Pro only)
const EMOTES = ["👍", "👎", "😂", "😡", "👏", "😴", "❤️", "💪"];

// Round states
const ROUND_STATE = {
  PENDING: "pending",
  ACTIVE: "active",
  COMPLETED: "completed"
};

// Turn-based status for individual debaters
const TURN_STATUS = {
  PENDING: "pending",      // Can start recording
  RECORDING: "recording",  // Currently recording
  COMPLETED: "completed",  // Finished this round
  WAITING: "waiting"       // Waiting for opponent (Blue waits for Red)
};

// Badge Component
const UserBadge = ({ badge }) => {
  if (!badge) return null;
  const badgeStyles = {
    pro: "bg-gradient-to-r from-amber-500 to-orange-500 text-white",
    access: "bg-gradient-to-r from-blue-500 to-cyan-500 text-white",
    admin: "bg-gradient-to-r from-purple-500 to-pink-500 text-white"
  };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full uppercase font-bold ${badgeStyles[badge] || ''}`}>
      {badge}
    </span>
  );
};

// Round Indicator Component
const RoundIndicator = ({ currentRound, rounds }) => {
  return (
    <div className="flex gap-2">
      {[1, 2, 3].map((round) => {
        const roundData = rounds?.[round - 1];
        const state = roundData?.state || ROUND_STATE.PENDING;
        const isActive = state === ROUND_STATE.ACTIVE;
        const isCompleted = state === ROUND_STATE.COMPLETED;
        
        return (
          <div 
            key={round}
            className={`flex-1 p-2 md:p-3 rounded-lg text-center transition-all ${
              isActive 
                ? 'bg-primary text-primary-foreground scale-105 shadow-lg shadow-primary/30' 
                : isCompleted 
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                  : 'bg-card/30 text-muted-foreground opacity-50'
            }`}
          >
            <div className="text-[10px] md:text-xs font-bold uppercase tracking-wider mb-0.5 flex items-center justify-center gap-1">
              {isCompleted && <CheckCircle className="w-3 h-3" />}
              Round {round}
            </div>
            <div className="text-xs md:text-sm font-semibold">{ROUND_NAMES[round]?.name}</div>
          </div>
        );
      })}
    </div>
  );
};

// Turn-Based Timer Component - Individual timer for each debater
const TurnBasedTimer = ({ startedAt, maxTime = 300, warningTime = 30, onTimeUp, onWarning, isRecording }) => {
  const [tick, setTick] = useState(0);
  const onTimeUpRef = useRef(onTimeUp);
  const onWarningRef = useRef(onWarning);
  const warningFiredRef = useRef(false);
  
  useEffect(() => {
    onTimeUpRef.current = onTimeUp;
    onWarningRef.current = onWarning;
  }, [onTimeUp, onWarning]);
  
  // Calculate time remaining from server timestamp
  const calculateTimeLeft = useCallback(() => {
    if (!startedAt || !isRecording) {
      return maxTime;
    }
    const started = new Date(startedAt).getTime();
    const elapsed = Math.floor((Date.now() - started) / 1000);
    return Math.max(0, maxTime - elapsed);
  }, [startedAt, maxTime, isRecording]);
  
  const timeLeft = calculateTimeLeft();
  
  useEffect(() => {
    if (!startedAt || !isRecording) {
      warningFiredRef.current = false;
      return;
    }
    
    const timer = setInterval(() => {
      setTick(t => t + 1);
      const remaining = calculateTimeLeft();
      
      // Fire warning at 30 seconds
      if (remaining <= warningTime && remaining > 0 && !warningFiredRef.current) {
        warningFiredRef.current = true;
        onWarningRef.current?.();
      }
      
      // Force stop at 0
      if (remaining <= 0) {
        clearInterval(timer);
        onTimeUpRef.current?.();
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, [startedAt, isRecording, calculateTimeLeft, warningTime]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const progress = (timeLeft / maxTime) * 100;
  const isWarning = timeLeft <= warningTime && timeLeft > 0;

  return (
    <div className="flex flex-col items-center gap-2">
      {/* Large time display */}
      <div className={`font-mono text-4xl md:text-5xl font-bold ${
        isWarning ? 'text-red-500 animate-pulse' : 'text-white'
      }`}>
        {minutes}:{seconds.toString().padStart(2, '0')}
      </div>
      {/* Progress bar */}
      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full transition-all duration-1000 ${
            progress > 50 ? 'bg-green-500' : progress > 20 ? 'bg-yellow-500' : 'bg-red-500'
          }`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

// Legacy Server-synced Timer Component (for spectators)
const RoundTimer = ({ roundData, maxTime = 300, onTimeUp }) => {
  const [tick, setTick] = useState(0);
  const onTimeUpRef = useRef(onTimeUp);
  
  // Update ref in effect to avoid render-time mutation
  useEffect(() => {
    onTimeUpRef.current = onTimeUp;
  }, [onTimeUp]);
  
  // Calculate time remaining from server timestamp (derived state, not stored)
  const calculateTimeLeft = useCallback(() => {
    if (!roundData?.started_at || roundData.state !== ROUND_STATE.ACTIVE) {
      return maxTime;
    }
    const startedAt = new Date(roundData.started_at).getTime();
    const elapsed = Math.floor((Date.now() - startedAt) / 1000);
    return Math.max(0, maxTime - elapsed);
  }, [roundData, maxTime]);
  
  const timeLeft = calculateTimeLeft();
  
  useEffect(() => {
    if (!roundData?.started_at || roundData.state !== ROUND_STATE.ACTIVE) {
      return;
    }
    
    // Force recalculation every second
    const timer = setInterval(() => {
      setTick(t => t + 1);
      const remaining = calculateTimeLeft();
      if (remaining <= 0) {
        clearInterval(timer);
        onTimeUpRef.current?.();
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, [roundData, calculateTimeLeft]);

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const progress = (timeLeft / maxTime) * 100;
  const isActive = roundData?.state === ROUND_STATE.ACTIVE;

  return (
    <div className="flex items-center gap-3">
      <div className="relative flex-1 h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className={`absolute inset-y-0 left-0 transition-all duration-1000 ${
            progress > 50 ? 'bg-green-500' : progress > 20 ? 'bg-yellow-500' : 'bg-red-500'
          }`}
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className={`font-mono text-lg font-bold min-w-[60px] ${
        isActive && timeLeft < 30 ? 'text-red-500 animate-pulse' : ''
      }`}>
        {minutes}:{seconds.toString().padStart(2, '0')}
      </div>
    </div>
  );
};

// Simplified Recorder Component - Handles both Video and Voice with 2-button flow
const DebateRecorder = React.forwardRef(({ 
  debateId, 
  roundNumber, 
  token, 
  debateType = "video", // "video" or "voice"
  side = "red",
  onStartRecording,
  onFinishRecording,
  onDonePressed, // New: callback when Done button is pressed (to finish turn completely)
  maxTime = 60, // Recording time limit
  turnStatus = "pending", // "pending" | "recording" | "completed" | "waiting"
  immersiveMode = false // When true, renders full-screen optimized for Focus Mode
}, ref) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const timerRef = useRef(null);
  const chunksRef = useRef([]);
  const containerRef = useRef(null);
  
  // Initialize status based on turnStatus
  const getInitialStatus = () => {
    if (turnStatus === "completed") return "done";
    if (turnStatus === "recording") return "recording";
    return "idle";
  };
  
  const [status, setStatus] = useState(getInitialStatus);
  const [errorMessage, setErrorMessage] = useState(null);
  const [recordingStartTime, setRecordingStartTime] = useState(null);
  const [timeLeft, setTimeLeft] = useState(maxTime);
  const [lastBeep, setLastBeep] = useState(0);
  const [previewActive, setPreviewActive] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [streamReady, setStreamReady] = useState(false);
  const [needsUserGesture, setNeedsUserGesture] = useState(false); // iOS play() fallback
  const frameCallbackRef = useRef(null); // For requestVideoFrameCallback
  const uploadAbortControllerRef = useRef(null); // For canceling uploads

  const isVideo = debateType === "video";
  
  // Helper: Attach stream to video element and play (iOS-safe, Mac-optimized)
  const attachAndPlayVideo = useCallback(async (stream) => {
    const videoEl = videoRef.current;
    if (!videoEl || !stream) {
      console.log('[FocusPreview] attachAndPlayVideo: missing videoEl or stream');
      return false;
    }
    
    // Detect Mac for special handling
    const isMac = /Macintosh|MacIntel|MacPPC|Mac68K/.test(navigator.platform);
    
    // Set all required attributes for iOS PWA standalone
    videoEl.srcObject = stream;
    videoEl.muted = true;
    videoEl.playsInline = true;
    videoEl.autoplay = true;
    videoEl.setAttribute('playsinline', '');
    
    console.log('[FocusPreview] srcObject set, waiting for loadedmetadata...');
    
    // Wait for loadedmetadata
    await new Promise((resolve) => {
      if (videoEl.readyState >= 1) {
        console.log('[FocusPreview] already loaded, readyState:', videoEl.readyState);
        resolve();
      } else {
        const onLoaded = () => {
          videoEl.removeEventListener('loadedmetadata', onLoaded);
          console.log('[FocusPreview] loadedmetadata fired');
          resolve();
        };
        videoEl.addEventListener('loadedmetadata', onLoaded);
        // Timeout fallback
        setTimeout(() => {
          videoEl.removeEventListener('loadedmetadata', onLoaded);
          console.log('[FocusPreview] loadedmetadata timeout, proceeding anyway');
          resolve();
        }, 2000);
      }
    });
    
    // Mac optimization: Match video element size to stream to avoid scaling overhead
    if (isMac && videoEl.videoWidth && videoEl.videoHeight) {
      console.log('[FocusPreview] Mac: Setting video element size to match stream:', 
        videoEl.videoWidth, 'x', videoEl.videoHeight);
      // Don't set explicit width/height as it can cause layout issues
      // Instead ensure object-fit handles it properly
    }
    
    // Attempt play()
    try {
      await videoEl.play();
      console.log('[FocusPreview] playing, dimensions:', videoEl.videoWidth, 'x', videoEl.videoHeight);
      
      // Mac optimization: Use requestVideoFrameCallback for smoother rendering if available
      if (isMac && 'requestVideoFrameCallback' in HTMLVideoElement.prototype) {
        console.log('[FocusPreview] Mac: Using requestVideoFrameCallback for smoother rendering');
        const scheduleFrame = () => {
          if (videoEl && !videoEl.paused && !videoEl.ended) {
            frameCallbackRef.current = videoEl.requestVideoFrameCallback(scheduleFrame);
          }
        };
        scheduleFrame();
      }
      
      return true;
    } catch (playErr) {
      console.log('[FocusPreview] play() failed:', playErr.name, playErr.message);
      if (playErr.name === 'NotAllowedError') {
        // iOS PWA needs user gesture - show tap button
        setNeedsUserGesture(true);
      }
      return false;
    }
  }, []);
  
  // Handler for "Tap to Start Camera" button (iOS user gesture requirement)
  const handleTapToStartCamera = useCallback(async () => {
    console.log('[FocusPreview] User tapped to start camera');
    if (videoRef.current && streamRef.current) {
      try {
        videoRef.current.srcObject = streamRef.current;
        await videoRef.current.play();
        console.log('[FocusPreview] playing after user tap');
        setNeedsUserGesture(false);
        setPreviewActive(true);
        setStreamReady(true);
      } catch (err) {
        console.error('[FocusPreview] play failed even after tap:', err);
        setPreviewError('Camera failed to start. Please try again.');
      }
    }
  }, []);
  
  // Initialize camera stream - runs when component mounts and is video mode
  // Does NOT depend on turnStatus - we want preview regardless of turn state
  useEffect(() => {
    let mounted = true;
    let initAttempts = 0;
    const maxAttempts = 3;
    
    const initializeStream = async () => {
      // Skip if not video mode or already have working stream
      if (!isVideo) {
        console.log('[FocusPreview] Not video mode, skipping');
        return;
      }
      
      // Skip if we already have a live stream and preview is active
      if (streamRef.current && previewActive) {
        const videoTrack = streamRef.current.getVideoTracks()[0];
        if (videoTrack && videoTrack.readyState === 'live') {
          console.log('[FocusPreview] Already have live stream, skipping init');
          return;
        }
      }
      
      console.log('[FocusPreview] Initializing stream, attempt:', initAttempts + 1);
      setPreviewError(null);
      
      try {
        // Detect platform for optimized settings
        const isMac = /Macintosh|MacIntel|MacPPC|Mac68K/.test(navigator.platform);
        const isDesktop = !(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
        
        // Mac desktops can have performance issues with high resolution
        // Use lower resolution for better frame rate on Mac
        const videoConstraints = {
          facingMode: "user",
          aspectRatio: { ideal: 16/9 }
        };
        
        if (isMac && isDesktop) {
          // Mac desktop: use 720p max for smoother preview
          console.log('[FocusPreview] Mac desktop detected - using optimized constraints');
          videoConstraints.width = { ideal: 1280, max: 1280 };
          videoConstraints.height = { ideal: 720, max: 720 };
          videoConstraints.frameRate = { ideal: 30, max: 30 };
        } else if (isDesktop) {
          // Other desktops
          videoConstraints.width = { ideal: 1280, min: 640 };
          videoConstraints.height = { ideal: 720, min: 360 };
          videoConstraints.frameRate = { ideal: 30, min: 24 };
        } else {
          // Mobile - can handle higher res
          videoConstraints.width = { ideal: 1280, min: 640 };
          videoConstraints.height = { ideal: 720, min: 360 };
          videoConstraints.frameRate = { ideal: 30, min: 24 };
        }
        
        // Request video + audio upfront
        const constraints = {
          video: videoConstraints,
          audio: true
        };
        
        console.log('[FocusPreview] Calling getUserMedia...');
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        
        if (!mounted) {
          console.log('[FocusPreview] Component unmounted, stopping stream');
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        
        const videoTrack = stream.getVideoTracks()[0];
        const audioTrack = stream.getAudioTracks()[0];
        
        console.log('[FocusPreview] Got stream:', {
          video: videoTrack ? `${videoTrack.label} (${videoTrack.readyState})` : 'none',
          audio: audioTrack ? `${audioTrack.label} (${audioTrack.readyState})` : 'none'
        });
        
        if (!videoTrack || videoTrack.readyState !== 'live') {
          throw new Error('Video track not live');
        }
        
        // Mute audio during preview
        if (audioTrack) {
          audioTrack.enabled = false;
          console.log('[FocusPreview] Audio muted for preview');
        }
        
        streamRef.current = stream;
        setStreamReady(true);
        
        // Attach and play
        const playSuccess = await attachAndPlayVideo(stream);
        
        if (playSuccess) {
          setPreviewActive(true);
          setNeedsUserGesture(false);
          console.log('[FocusPreview] ✓ Preview active and visible');
        } else {
          // Stream is ready even if autoplay blocked
          console.log('[FocusPreview] Stream ready, but play may need user gesture');
        }
        
      } catch (err) {
        console.error('[FocusPreview] Init failed:', err.name, err.message);
        
        initAttempts++;
        if (mounted && initAttempts < maxAttempts) {
          console.log('[FocusPreview] Retrying in 500ms...');
          setTimeout(initializeStream, 500);
          return;
        }
        
        setPreviewError(err.message || 'Camera access failed');
        setPreviewActive(false);
        setStreamReady(false);
      }
    };
    
    // Small delay to ensure DOM is ready
    const timer = setTimeout(initializeStream, 100);
    
    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [isVideo, attachAndPlayVideo]); // Removed turnStatus, previewActive, needsUserGesture deps
  
  // Cleanup stream on unmount only - use ref to check status without dependency
  const statusRef = useRef(status);
  statusRef.current = status;
  
  useEffect(() => {
    return () => {
      console.log('[FocusPreview] Component unmounting, status:', statusRef.current);
      // Only cleanup if not recording or uploading
      if (streamRef.current && statusRef.current !== "recording" && statusRef.current !== "uploading") {
        streamRef.current.getTracks().forEach(track => {
          console.log('[FocusPreview] Stopping track:', track.kind);
          track.stop();
        });
        streamRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      // Cleanup requestVideoFrameCallback
      if (frameCallbackRef.current && videoRef.current) {
        videoRef.current.cancelVideoFrameCallback?.(frameCallbackRef.current);
        frameCallbackRef.current = null;
      }
    };
  }, []); // Empty deps - only runs on unmount
  
  // Timer effect: runs when recording and recordingStartTime is set
  useEffect(() => {
    // Only run when recording AND we have a start time
    if (status !== "recording" || recordingStartTime === null) {
      return;
    }
    
    console.log('[Timer] Starting countdown, maxTime:', maxTime);
    
    const tick = () => {
      const now = performance.now();
      const elapsedMs = now - recordingStartTime;
      const remainingSec = Math.max(0, Math.ceil((maxTime * 1000 - elapsedMs) / 1000));
      
      setTimeLeft(remainingSec);
      
      // Log every 5 seconds to reduce noise
      if (remainingSec % 5 === 0 || remainingSec <= 5) {
        console.log('[Timer] remaining:', remainingSec, 's');
      }
      
      // Haptic for last 5 seconds
      if (remainingSec <= 5 && remainingSec > 0 && remainingSec !== lastBeep) {
        setLastBeep(remainingSec);
        if (navigator.vibrate) {
          navigator.vibrate(remainingSec === 1 ? [100, 50, 100] : 50);
        }
      }
      
      if (remainingSec <= 0) {
        console.log('[Timer] Time expired');
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    
    tick(); // Immediate first tick
    timerRef.current = setInterval(tick, 200); // 200ms for smoother updates
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [status, recordingStartTime, maxTime, lastBeep]);
  
  // Reset timeLeft when not recording
  const displayTime = status === "recording" ? timeLeft : maxTime;

  // Draw audio waveform for voice-only mode
  const drawWaveform = useCallback(() => {
    if (!analyserRef.current || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);
      analyser.getByteTimeDomainData(dataArray);
      
      ctx.fillStyle = '#18181b';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.lineWidth = 3;
      ctx.strokeStyle = side === 'red' ? '#e11d48' : '#3b82f6';
      ctx.beginPath();
      
      const sliceWidth = canvas.width / bufferLength;
      let x = 0;
      
      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = v * canvas.height / 2;
        
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
        x += sliceWidth;
      }
      
      ctx.lineTo(canvas.width, canvas.height / 2);
      ctx.stroke();
    };
    
    draw();
  }, [side]);

  // Start Recording - uses existing stream, enables audio, starts MediaRecorder
  // Timer ONLY starts when mediaRecorder.onstart fires
  const handleStartRecording = useCallback(async () => {
    setErrorMessage(null);
    console.log('[Recorder] Starting recording process...');
    
    // Check browser support
    if (!navigator.mediaDevices?.getUserMedia) {
      setErrorMessage("Recording not supported in this browser");
      setStatus("error");
      return false;
    }

    try {
      // Lock orientation on mobile during recording to prevent sideways videos
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      // Store initial orientation for potential playback correction
      const initialOrientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
      console.log('[Recorder] Initial orientation:', initialOrientation);
      
      if (isMobile) {
        // Try multiple methods to lock orientation
        if (screen.orientation?.lock) {
          try {
            // Lock to current orientation
            const orientationType = screen.orientation.type.includes('portrait') 
              ? 'portrait-primary' 
              : 'landscape-primary';
            await screen.orientation.lock(orientationType);
            console.log('[Recorder] Orientation locked to:', orientationType);
          } catch (lockErr) {
            console.log('[Recorder] screen.orientation.lock failed:', lockErr.message);
            // Fallback: Try natural orientation
            try {
              await screen.orientation.lock('natural');
              console.log('[Recorder] Orientation locked to natural');
            } catch (e2) {
              console.log('[Recorder] Natural lock also failed');
            }
          }
        }
        
        // iOS Safari doesn't support orientation lock, so we'll handle it differently
        // Store the starting orientation for metadata
        if (!screen.orientation?.lock) {
          console.log('[Recorder] Orientation lock not supported, will rely on device orientation');
        }
      }
      
      let recordingStream = streamRef.current;
      
      // Use existing stream (video+audio already acquired, audio was disabled)
      if (recordingStream && isVideo) {
        console.log('[Recorder] Using existing stream');
        
        // Verify video track is still live
        const videoTrack = recordingStream.getVideoTracks()[0];
        if (!videoTrack || videoTrack.readyState !== 'live') {
          console.log('[Recorder] Video track dead, need fresh stream');
          recordingStream = null;
        } else {
          // Enable audio track for recording
          const audioTrack = recordingStream.getAudioTracks()[0];
          if (audioTrack) {
            audioTrack.enabled = true;
            console.log('[Recorder] Audio track enabled:', audioTrack.label, audioTrack.readyState);
          } else {
            console.log('[Recorder] No audio track in stream, will record video-only');
          }
          
          // Ensure preview stays visible - re-bind srcObject if needed
          if (videoRef.current) {
            if (videoRef.current.srcObject !== recordingStream) {
              console.log('[Recorder] Re-binding srcObject to ensure preview');
              videoRef.current.srcObject = recordingStream;
            }
            // Always try to play to ensure preview is visible
            try {
              await videoRef.current.play();
              console.log('[Recorder] Preview playing');
            } catch (e) {
              console.log('[Recorder] Play on rebind:', e.message);
            }
          }
        }
      }
      
      // Fallback: request fresh stream if none available
      if (!recordingStream) {
        console.log('[Recorder] Requesting fresh stream (no existing stream)');
        
        // Detect Mac for optimized constraints
        const isMac = /Macintosh|MacIntel|MacPPC|Mac68K/.test(navigator.platform);
        const isDesktop = !(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
        
        const videoConstraints = {
          facingMode: "user",
          aspectRatio: { ideal: 16/9 }
        };
        
        if (isMac && isDesktop) {
          // Mac desktop: use 720p max for smoother recording
          console.log('[Recorder] Mac desktop - using optimized constraints');
          videoConstraints.width = { ideal: 1280, max: 1280 };
          videoConstraints.height = { ideal: 720, max: 720 };
          videoConstraints.frameRate = { ideal: 30, max: 30 };
        } else {
          videoConstraints.width = { ideal: 1280, min: 640 };
          videoConstraints.height = { ideal: 720, min: 360 };
          videoConstraints.frameRate = { ideal: 30, min: 24 };
        }
        
        const constraints = {
          video: videoConstraints,
          audio: true
        };
        
        recordingStream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = recordingStream;
        
        // Attach to video element
        if (videoRef.current) {
          const playSuccess = await attachAndPlayVideo(recordingStream);
          console.log('[Recorder] Fresh stream attached, play success:', playSuccess);
        }
      }
      
      // Log actual capture settings
      const videoTrack = recordingStream.getVideoTracks()[0];
      const settings = videoTrack?.getSettings();
      console.log('[Recorder] Capture settings:', settings?.width, 'x', settings?.height, 'fps:', settings?.frameRate);
      
      // Setup MediaRecorder
      console.log('[Recorder] Setting up MediaRecorder...');
      chunksRef.current = [];
      
      // Find best supported format
      const formats = isVideo 
        ? [
            'video/mp4;codecs=avc1.42E01E,mp4a.40.2', // H.264 + AAC
            'video/mp4',
            'video/webm;codecs=vp9,opus',
            'video/webm;codecs=vp8,opus', 
            'video/webm;codecs=h264',
            'video/webm'
          ]
        : ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm'];
      
      let supportedMime = '';
      console.log('[Recorder] Testing supported formats...');
      for (const format of formats) {
        const supported = MediaRecorder.isTypeSupported(format);
        console.log(`[Recorder] ${format}: ${supported ? 'SUPPORTED' : 'not supported'}`);
        if (supported && !supportedMime) {
          supportedMime = format;
        }
      }
      
      // Configure recorder with higher bitrate for better quality
      const recorderOptions = {
        videoBitsPerSecond: 4000000, // 4 Mbps for better quality (especially mobile→desktop playback)
        audioBitsPerSecond: 128000   // 128 kbps audio
      };
      
      if (supportedMime) {
        recorderOptions.mimeType = supportedMime;
        console.log('[Recorder] Using mimeType:', supportedMime);
      } else {
        console.log('[Recorder] Using browser default mimeType');
      }
      
      const mediaRecorder = new MediaRecorder(recordingStream, recorderOptions);
      console.log('[Recorder] MediaRecorder created, state:', mediaRecorder.state, 
        'mimeType:', mediaRecorder.mimeType, 'bitrate:', recorderOptions.videoBitsPerSecond);
      
      // Track data chunks
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
          // Reduce logging noise - only log occasionally
          if (chunksRef.current.length % 4 === 0) {
            console.log('[Recorder] chunks:', chunksRef.current.length, 'latest size:', e.data.size);
          }
        }
      };
      
      mediaRecorder.onerror = (e) => {
        console.error('[Recorder] MediaRecorder error:', e);
        // Stop timer on error
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setStatus("error");
        setErrorMessage("Recording error: " + (e.error?.message || 'Unknown error'));
      };
      
      // CRITICAL: Start timer ONLY when onstart fires
      mediaRecorder.onstart = () => {
        console.log('[Recorder] ★ onstart fired - recording has truly started');
        // Use performance.now() for wall-clock timer
        setRecordingStartTime(performance.now());
        setTimeLeft(maxTime); // Reset display
        setLastBeep(0);
      };
      
      // Setup audio waveform for voice-only mode
      if (!isVideo) {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const source = audioContext.createMediaStreamSource(recordingStream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        analyserRef.current = analyser;
        drawWaveform();
      }
      
      mediaRecorderRef.current = mediaRecorder;
      
      // Start recording with 500ms chunks for smoother preview/recording sync
      // Smaller chunks = more frequent dataavailable events = smoother operation
      mediaRecorder.start(500);
      console.log('[Recorder] MediaRecorder.start() called, state:', mediaRecorder.state);
      
      // Set status to recording (timer will start when onstart fires)
      setStatus("recording");
      
      // Scroll video into view
      containerRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
      
      // Notify parent
      onStartRecording?.();
      return true;
      
    } catch (err) {
      console.error("[Recorder] Error:", err.name, err.message, err);
      let msg = "Could not start recording.";
      if (err.name === "NotAllowedError") {
        msg = isVideo 
          ? "Camera/microphone access denied. Please allow in browser settings."
          : "Microphone access denied. Please allow in browser settings.";
      } else if (err.name === "NotFoundError") {
        msg = isVideo ? "No camera or microphone found." : "No microphone found.";
      } else if (err.name === "NotSupportedError") {
        msg = "Recording format not supported on this device. Try a different browser.";
      } else if (err.name === "NotReadableError" || err.name === "AbortError") {
        msg = "Camera/microphone is in use by another app. Please close other apps using camera.";
      } else if (err.name === "OverconstrainedError") {
        msg = "Camera doesn't support the requested settings. Trying again...";
      } else if (err.name === "SecurityError") {
        msg = "Camera access blocked due to security policy. Please use HTTPS.";
      } else {
        msg = `Recording error: ${err.name || 'Unknown'} - ${err.message || 'Please try again'}`;
      }
      setErrorMessage(msg);
      setStatus("error");
      return false;
    }
  }, [isVideo, maxTime, attachAndPlayVideo, drawWaveform, onStartRecording]);

  // Finish Recording - stop + upload + mark complete
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFinishRecording = useCallback(async () => {
    console.log('[Recorder] handleFinishRecording called');
    
    if (!mediaRecorderRef.current) {
      console.log('[Recorder] No mediaRecorder to stop');
      return false;
    }
    
    const recorderState = mediaRecorderRef.current.state;
    console.log('[Recorder] MediaRecorder state:', recorderState);
    
    if (recorderState !== 'recording') {
      console.log('[Recorder] Not recording, nothing to stop');
      return false;
    }
    
    console.log('[Recorder] Stopping recording...');
    
    // Stop timer immediately
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
      console.log('[Timer] stopped');
    }
    
    setStatus("uploading");
    setUploadProgress(0);
    
    // Stop recording
    return new Promise((resolve) => {
      mediaRecorderRef.current.onstop = async () => {
        console.log('[Recorder] onstop fired');
        
        // Disable audio track (don't stop video - may still be showing)
        if (streamRef.current) {
          const audioTrack = streamRef.current.getAudioTracks()[0];
          if (audioTrack) {
            audioTrack.enabled = false;
            console.log('[Recorder] Audio track disabled');
          }
        }
        
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        
        // Unlock screen orientation
        if (screen.orientation?.unlock) {
          try {
            screen.orientation.unlock();
            console.log('[Recorder] Screen orientation unlocked');
          } catch (unlockErr) {
            console.log('[Recorder] Could not unlock orientation:', unlockErr.message);
          }
        }
        
        // Create blob using ACTUAL mimeType from MediaRecorder
        const actualMimeType = mediaRecorderRef.current?.mimeType || (isVideo ? 'video/webm' : 'audio/webm');
        const blob = new Blob(chunksRef.current, { type: actualMimeType });
        console.log('[Recorder] Final blob - size:', blob.size, 'bytes, type:', actualMimeType);
        
        // Check if blob has content
        if (blob.size === 0) {
          console.error("Recording blob is empty");
          setErrorMessage("Recording failed - no data captured. Please try again.");
          setStatus("error");
          resolve(false);
          return;
        }
        
        const fileSizeMB = (blob.size / (1024 * 1024)).toFixed(2);
        console.log(`Uploading recording: ${blob.size} bytes (${fileSizeMB} MB)`);
        
        // Upload with retry logic
        const maxRetries = 3;
        let lastError = null;
        
        // Create abort controller for this upload
        uploadAbortControllerRef.current = new AbortController();
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            setUploadProgress(0);
            const formData = new FormData();
            formData.append('video', blob, `round_${roundNumber}_${side}.webm`);
            formData.append('round_number', roundNumber.toString());
            formData.append('side', side);
            
            console.log(`Upload attempt ${attempt}/${maxRetries}...`);
            
            await axios.post(
              `${API}/debates/${debateId}/rounds/${roundNumber}/upload`,
              formData,
              { 
                headers: { 
                  Authorization: `Bearer ${token}`, 
                  'Content-Type': 'multipart/form-data' 
                },
                timeout: 300000, // 5 minute timeout for large files uploading to cloud
                signal: uploadAbortControllerRef.current.signal,
                onUploadProgress: (progressEvent) => {
                  const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                  setUploadProgress(percentCompleted);
                  console.log(`Upload progress: ${percentCompleted}%`);
                }
              }
            );
            
            console.log("Upload successful!");
            setUploadProgress(100);
            setStatus("done");
            toast.success("Recording uploaded successfully!");
            uploadAbortControllerRef.current = null;
            onFinishRecording?.();
            resolve(true);
            return;
          } catch (err) {
            // Check if cancelled by user
            if (axios.isCancel(err) || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
              console.log("Upload cancelled by user");
              setStatus("idle");
              setUploadProgress(0);
              toast.info("Upload cancelled");
              uploadAbortControllerRef.current = null;
              resolve(false);
              return;
            }
            
            console.error(`Upload attempt ${attempt} failed:`, err);
            console.error(`Error details:`, {
              status: err?.response?.status,
              statusText: err?.response?.statusText,
              data: err?.response?.data,
              message: err?.message
            });
            lastError = err;
            
            if (attempt < maxRetries) {
              // Wait before retry (2s, 4s, 6s)
              const waitTime = attempt * 2000;
              console.log(`Retrying in ${waitTime/1000}s...`);
              await new Promise(r => setTimeout(r, waitTime));
            }
          }
        }
        
        // All retries failed
        console.error("All upload attempts failed:", lastError);
        const errorDetail = lastError?.response?.data?.detail || lastError?.message || "Network error";
        setErrorMessage(`Upload failed: ${errorDetail}. Please check your connection and try again.`);
        setStatus("error");
        
        // Show prominent toast notification
        toast.error(`Video upload failed after 3 attempts. ${errorDetail}`, {
          duration: 10000,
          description: "Your recording was not saved. Please try recording again."
        });
        
        resolve(false);
      };
      
      mediaRecorderRef.current.stop();
    });
  }, [isVideo, debateId, roundNumber, side, token, onFinishRecording]);

  // Handler for Done button - can be called externally via ref
  const handleDone = useCallback(async () => {
    console.log('[Recorder] handleDone called, status:', status, 'mediaRecorder:', !!mediaRecorderRef.current);
    
    // Check if we have a recorder and are in a recordable state
    if (!mediaRecorderRef.current) {
      console.log('[Recorder] No mediaRecorder, cannot stop');
      return false;
    }
    
    // Allow stopping even if status check is stale
    const recorderState = mediaRecorderRef.current.state;
    console.log('[Recorder] MediaRecorder state:', recorderState);
    
    if (recorderState !== 'recording') {
      console.log('[Recorder] Not currently recording (state:', recorderState, ')');
      return false;
    }
    
    // First upload the video and WAIT for it to complete
    const uploadSuccess = await handleFinishRecording();
    console.log('[Recorder] Upload result:', uploadSuccess);
    
    // Only mark turn as finished if upload succeeded
    if (uploadSuccess && onDonePressed) {
      await onDonePressed();
    }
    return uploadSuccess;
  }, [status, handleFinishRecording, onDonePressed]);

  // Cancel upload handler
  const handleCancelUpload = useCallback(() => {
    if (uploadAbortControllerRef.current) {
      console.log('[Recorder] Cancelling upload...');
      uploadAbortControllerRef.current.abort();
    }
  }, []);

  // Expose methods via ref for parent component - must be after function definitions
  React.useImperativeHandle(ref, () => ({
    startRecording: handleStartRecording,
    finishRecording: handleFinishRecording,
    handleDone: handleDone,
    cancelUpload: handleCancelUpload,
    getStatus: () => status,
    getTimeLeft: () => timeLeft // Expose timer for Focus Mode header
  }), [handleStartRecording, handleFinishRecording, handleDone, handleCancelUpload, status, timeLeft]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      // Unlock screen orientation on cleanup
      if (screen.orientation?.unlock) {
        try {
          screen.orientation.unlock();
        } catch (e) {
          // Ignore unlock errors on cleanup
        }
      }
    };
  }, []);
  
  // Warn user if they try to leave during upload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (status === "uploading") {
        e.preventDefault();
        e.returnValue = "Your video is still uploading. If you leave now, your recording will be lost!";
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [status]);

  const borderColor = side === 'red' ? 'border-red-500/50' : 'border-blue-500/50';
  const bgGradient = side === 'red' ? 'from-red-500/20' : 'from-blue-500/20';
  const accentColor = side === 'red' ? 'text-red-400' : 'text-blue-400';
  
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  // Track orientation for UI display
  const [isPortrait, setIsPortrait] = useState(window.matchMedia('(orientation: portrait)').matches);
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(orientation: portrait)');
    const handler = (e) => setIsPortrait(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return (
    <div ref={containerRef} className={`relative h-full flex flex-col ${immersiveMode ? 'w-full' : ''}`}>
      {/* Mobile Landscape Tip - Show when portrait on MOBILE during recording, hide in immersive */}
      {status === "recording" && isPortrait && !immersiveMode && isMobile && (
        <div className="absolute bottom-16 left-1/2 -translate-x-1/2 bg-yellow-500/90 text-black text-xs px-3 py-1.5 rounded-full z-30 flex items-center gap-2 whitespace-nowrap">
          <Smartphone className="w-3 h-3" />
          <span>Rotate for better view</span>
        </div>
      )}
      
      {/* Preview Error Indicator */}
      {previewError && isVideo && (
        <div className="absolute inset-0 flex flex-col items-center justify-center z-20 bg-black/80">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mb-3" />
          <p className="text-white text-sm text-center px-4">{previewError}</p>
          <p className="text-muted-foreground text-xs mt-2">Tap Record to try again</p>
        </div>
      )}
      
      {/* iOS "Tap to Start Camera" overlay - shown when play() needs user gesture */}
      {needsUserGesture && isVideo && !previewError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center z-20 bg-black/90">
          <button
            onClick={handleTapToStartCamera}
            className="flex flex-col items-center gap-3 p-6 rounded-xl bg-zinc-800 hover:bg-zinc-700 transition-colors"
          >
            <Camera className="w-16 h-16 text-white" />
            <span className="text-white text-lg font-medium">Tap to Start Camera</span>
            <span className="text-muted-foreground text-sm">Camera ready, tap to begin preview</span>
          </button>
        </div>
      )}
      
      {/* Preview Area - Full coverage in immersive mode, 16:9 otherwise */}
      <div className={`flex-1 overflow-hidden bg-black relative ${
        immersiveMode 
          ? 'rounded-none border-0 w-full h-full' 
          : `rounded-lg border-2 ${borderColor} aspect-video`
      }`}>
        {isVideo ? (
          <video 
            ref={videoRef}
            autoPlay 
            muted 
            playsInline
            className="w-full h-full mirror bg-black object-contain"
            style={{ 
              objectFit: 'contain',
              /* GPU acceleration for smooth desktop preview */
              transform: 'translateZ(0)',
              willChange: 'transform',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              perspective: 1000
            }}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center p-4">
            {/* Voice Mode Icon */}
            <Mic className={`w-16 h-16 ${accentColor} mb-4 ${status === 'recording' ? 'animate-pulse' : ''}`} />
            <canvas 
              ref={canvasRef}
              width={400}
              height={100}
              className="w-full max-w-md rounded bg-zinc-900/50"
            />
            <p className="text-xs text-muted-foreground mt-2">Audio waveform</p>
          </div>
        )}
        
        {/* Recording indicator + Timer overlay - positioned at top center of video */}
        {/* Hide in immersive mode since Focus Mode header has its own timer */}
        {status === "recording" && !immersiveMode && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/90 px-3 py-1.5 rounded-full z-30">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className={`font-mono text-lg font-bold ${displayTime <= 5 ? 'text-red-500 animate-pulse' : 'text-white'}`}>
              {Math.floor(displayTime / 60)}:{(displayTime % 60).toString().padStart(2, '0')}
            </span>
          </div>
        )}
        
        {/* Done button overlay during recording - hide in immersive mode (shown in footer instead) */}
        {status === "recording" && !immersiveMode && (
          <button
            onClick={handleDone}
            className="absolute bottom-12 right-3 bg-green-600 hover:bg-green-700 text-white font-bold px-5 py-3 rounded-full flex items-center gap-2 z-10 shadow-lg text-lg"
          >
            <CheckCircle className="w-6 h-6" />
            Done
          </button>
        )}
        
        {/* Orientation indicator - only when idle on MOBILE and not yet started */}
        {isVideo && status === "idle" && turnStatus !== "completed" && !previewActive && isMobile && (
          <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/70 px-2 py-1 rounded-full z-10">
            <Smartphone className={`w-3 h-3 text-white ${isPortrait ? '' : 'rotate-90'}`} />
            <span className="text-xs text-white">{isPortrait ? '9:16' : '16:9'}</span>
          </div>
        )}
        
        {/* Status overlays - Hide when preview is active */}
        {status === "idle" && turnStatus !== "completed" && !previewActive && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80">
            <div className="text-center p-4">
              {isVideo ? (
                <Camera className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              ) : (
                <Mic className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              )}
              <p className="text-sm text-muted-foreground mb-1">
                {isVideo ? "Camera preview will appear here" : "Audio waveform will appear here"}
              </p>
              <p className="text-xs text-muted-foreground mb-2">
                Click &quot;Start Recording&quot; when ready
              </p>
              {isVideo && isMobile && (
                <p className="text-xs text-yellow-400">
                  📱 {isPortrait ? 'Portrait (9:16)' : 'Landscape (16:9)'} — Rotate device to change
                </p>
              )}
            </div>
          </div>
        )}
        
        {status === "uploading" && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/90">
            <div className="text-center w-72 p-4">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-lg text-white font-bold mb-2">Uploading Video...</p>
              <div className="w-full bg-gray-700 rounded-full h-3 mt-3">
                <div 
                  className="bg-green-500 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-sm text-white mt-2 font-medium">{uploadProgress}%</p>
              <div className="mt-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
                <p className="text-sm text-red-400 font-bold">⚠️ DO NOT LEAVE THIS PAGE</p>
                <p className="text-xs text-red-300 mt-1">Your video will be lost if you navigate away</p>
              </div>
            </div>
          </div>
        )}
        
        {(status === "done" || turnStatus === "completed") && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80">
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-sm text-green-400 font-medium">Turn Complete!</p>
              <p className="text-xs text-muted-foreground mt-1">Waiting for opponent...</p>
            </div>
          </div>
        )}
        
        {status === "error" && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80">
            <div className="text-center p-4">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <p className="text-sm text-red-400 mb-3">{errorMessage}</p>
              <Button size="sm" variant="outline" onClick={() => setStatus("idle")}>
                Try Again
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

// DebateRecorder is now the primary recording component (defined above)
// Old VideoRecorder has been removed

// Emote Picker Component (Pro only) - Fixed position at bottom
const EmotePicker = ({ debateId, token, isPro }) => {
  const [sending, setSending] = useState(false);
  const [recentEmotes, setRecentEmotes] = useState([]);

  const sendEmote = async (emote) => {
    if (!isPro) {
      toast.error("Emotes are for Pro Debaters only");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/emotes`, { debate_id: debateId, emote }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentEmotes(prev => [{ emote, id: Date.now() }, ...prev.slice(0, 9)]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send emote");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="relative">
      <div className="flex flex-wrap gap-1 justify-center">
        {EMOTES.map((emote) => (
          <button
            key={emote}
            onClick={() => sendEmote(emote)}
            disabled={sending || !isPro}
            className={`text-xl md:text-2xl p-1.5 md:p-2 rounded-lg transition-all hover:scale-125 hover:bg-white/10 ${
              !isPro ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title={isPro ? `Send ${emote}` : "Pro Debaters only"}
          >
            {emote}
          </button>
        ))}
      </div>
      {!isPro && (
        <p className="text-[10px] text-center text-muted-foreground mt-1">
          <Lock className="w-3 h-3 inline mr-1" />
          Pro only
        </p>
      )}
      {/* Floating emotes animation - high z-index to float above all content */}
      <div className="fixed bottom-20 left-0 right-0 pointer-events-none z-[60] overflow-visible h-[300px]">
        {recentEmotes.map((e) => (
          <span 
            key={e.id} 
            className="absolute text-3xl md:text-4xl animate-float-up"
            style={{ left: `${Math.random() * 80 + 10}%`, bottom: 0 }}
          >
            {e.emote}
          </span>
        ))}
      </div>
    </div>
  );
};

// Turn-Based Recording Controls Component - Simplified 2-button flow
const TurnBasedControls = ({ 
  debate, 
  userSide, 
  currentRound, 
  currentRoundData, 
  roundInfo,
  isRecording,
  setIsRecording,
  startingRound,
  isPro,
  token,
  debateId,
  onStartRound,
  onFinishRound,
  onTimeUp,
  onTimeWarning,
  onConcede,
  recorderRef,
  onFocusModeToggle // New prop for Focus Mode
}) => {
  const myStatus = currentRoundData?.[`${userSide}_status`] || "pending";
  const myStartedAt = currentRoundData?.[`${userSide}_started_at`];
  const opponentSide = userSide === "red" ? "blue" : "red";
  const opponentStatus = currentRoundData?.[`${opponentSide}_status`] || "pending";
  const opponentUsername = debate[`${opponentSide}_corner_username`] || opponentSide.charAt(0).toUpperCase() + opponentSide.slice(1);
  const maxTime = debate.max_round_time || 300;
  const responseDeadline = debate.response_deadline;
  const debateType = debate.debate_type || "video";
  const isVoice = debateType === "voice";
  const isTestDebate = debate.is_test_debate;
  
  // Detect landscape mode on mobile
  const [isLandscapeMobile, setIsLandscapeMobile] = useState(false);
  
  useEffect(() => {
    const checkOrientation = () => {
      const isLandscape = window.matchMedia('(orientation: landscape)').matches;
      const isMobile = window.innerWidth < 1024 && window.innerHeight < 600;
      setIsLandscapeMobile(isLandscape && isMobile);
    };
    
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);
  
  // Calculate time until response deadline
  const getDeadlineInfo = () => {
    if (!responseDeadline) return null;
    const deadline = new Date(responseDeadline);
    const now = new Date();
    const remaining = deadline - now;
    if (remaining <= 0) return { expired: true, text: "Response deadline passed" };
    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    return { expired: false, text: `${hours}h ${minutes}m remaining to respond` };
  };
  
  // State for watching opponent's video
  const [watchingOpponent, setWatchingOpponent] = useState(false);
  
  // Get opponent's video URL from current round
  const opponentVideoUrl = currentRoundData?.[`${opponentSide}_video_url`];
  
  const deadlineInfo = getDeadlineInfo();
  
  // For test debates, always allow recording (no turn-based waiting)
  // For normal debates, follow turn-based logic
  const isMyTurn = isTestDebate 
    ? myStatus === "pending" || myStatus === "recording"
    : userSide === "red" 
      ? myStatus === "pending" || myStatus === "recording"
      : (myStatus === "pending" || myStatus === "recording") && currentRoundData?.red_status === "completed";
  
  const isWaitingForOpponent = !isTestDebate && userSide === "blue" && currentRoundData?.red_status !== "completed" && myStatus === "waiting";
  const amIRecording = myStatus === "recording";
  const haveICompleted = myStatus === "completed";
  const hasOpponentCompleted = opponentStatus === "completed";
  const bothCompleted = haveICompleted && hasOpponentCompleted;

  // Handler for Done button - stops recording and finishes turn
  const handleDone = async () => {
    // Immediately update UI to show we're finishing
    setIsRecording(false);
    
    // First stop the recorder and upload
    if (recorderRef?.current) {
      const success = await recorderRef.current.finishRecording();
      if (success) {
        // Then notify backend that turn is finished - AWAIT to ensure completion
        if (onFinishRound) {
          await onFinishRound();
        }
      } else {
        // If upload failed, still try to finish the round
        if (onFinishRound) {
          await onFinishRound();
        }
      }
    } else {
      // Fallback if no recorder ref
      if (onFinishRound) {
        await onFinishRound();
      }
    }
  };

  // Red is waiting for Blue
  if (userSide === "red" && haveICompleted && !hasOpponentCompleted) {
    return (
      <div className="bg-card/30 rounded-lg border border-white/10 p-4">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 text-green-500 mb-2">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Your turn complete!</span>
          </div>
          <div className="flex items-center justify-center gap-2 text-muted-foreground mb-2">
            <Clock className="w-4 h-4 animate-pulse" />
            <span>Waiting for {opponentUsername} to record...</span>
          </div>
          {deadlineInfo && (
            <p className={`text-xs ${deadlineInfo.expired ? 'text-red-400' : 'text-muted-foreground'}`}>
              ⏰ {deadlineInfo.text}
            </p>
          )}
        </div>
      </div>
    );
  }
  
  // Blue waiting for Red to finish
  if (isWaitingForOpponent) {
    return (
      <div className="bg-card/30 rounded-lg border border-white/10 p-4">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 text-muted-foreground mb-2">
            <Clock className="w-4 h-4 animate-pulse" />
            <span className="font-medium">Waiting for {debate.red_corner_username || "Red"} to finish...</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Turn order: Red records first, then it&apos;s your turn
          </p>
        </div>
      </div>
    );
  }
  
  // Currently recording - show timer and Done button
  if (amIRecording) {
    return (
      <div className="bg-card/30 rounded-lg border border-white/10 p-4 mb-16">
        {/* Large prominent timer at top - visible on mobile */}
        <div className="mb-4 p-3 bg-black/50 rounded-lg border border-red-500/30">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="w-4 h-4 bg-red-500 rounded-full animate-pulse" />
            <span className="text-lg font-bold text-red-400">RECORDING</span>
          </div>
          <TurnBasedTimer 
            startedAt={myStartedAt}
            maxTime={maxTime}
            warningTime={30}
            onTimeUp={onTimeUp}
            onWarning={onTimeWarning}
            isRecording={true}
          />
        </div>
        
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-muted-foreground">{roundInfo?.name}</span>
          <div className="flex items-center gap-2">
            <HighlightMarker 
              debateId={debateId} 
              token={token} 
              isPro={isPro}
              isRecording={isRecording}
            />
            {/* Large Done button */}
            <Button 
              onClick={handleDone}
              className="bg-green-600 hover:bg-green-700 text-lg px-6 py-2"
              size="lg"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              Done
            </Button>
          </div>
        </div>
        <p className="text-xs text-center text-muted-foreground mt-3">
          Tap Done when finished, or wait for timer to auto-stop
        </p>
      </div>
    );
  }
  
  // Ready to start (my turn, not recording yet)
  if (isMyTurn && myStatus === "pending") {
    // For Red in Round 2+, get Blue's video from previous round
    const previousRoundIndex = currentRound - 2; // 0-indexed, previous round
    const previousRoundData = debate.rounds?.[previousRoundIndex];
    const previousOpponentVideoUrl = previousRoundData?.[`${opponentSide}_video_url`];
    
    // Determine which video to show - current round for Blue, previous for Red in R2+
    const videoToWatch = userSide === "blue" 
      ? opponentVideoUrl  // Blue watches Red's current round
      : (currentRound > 1 ? previousOpponentVideoUrl : null);  // Red watches Blue's previous round
    
    const watchPromptText = userSide === "blue"
      ? `${debate.red_corner_username || "Red"} has recorded their ${roundInfo?.name}!`
      : `${debate.blue_corner_username || "Blue"} finished Round ${currentRound - 1}!`;
    
    const isWatchingRed = userSide === "blue";
    const opponentName = userSide === "blue" 
      ? debate.red_corner_username 
      : debate.blue_corner_username;
    
    return (
      <div className="bg-card/30 rounded-lg border border-white/10 p-4">
        <div className="text-center">
          {/* Turn notification with urgency for Blue */}
          {userSide === "blue" && deadlineInfo && (
            <div className={`mb-3 px-3 py-2 rounded-lg ${deadlineInfo.expired ? 'bg-red-500/20 border border-red-500/30' : 'bg-yellow-500/20 border border-yellow-500/30'}`}>
              <p className={`text-sm font-medium ${deadlineInfo.expired ? 'text-red-400' : 'text-yellow-400'}`}>
                🔔 It&apos;s your turn! {deadlineInfo.text}
              </p>
            </div>
          )}
          
          {/* Watch Opponent's Response - Show when there's a video to watch */}
          {videoToWatch && !watchingOpponent && (
            <div className={`mb-4 p-3 rounded-lg ${isWatchingRed ? 'bg-red-500/10 border border-red-500/30' : 'bg-blue-500/10 border border-blue-500/30'}`}>
              <p className={`text-sm mb-2 ${isWatchingRed ? 'text-red-400' : 'text-blue-400'}`}>
                🎬 {watchPromptText}
              </p>
              <Button 
                onClick={() => {
                  setWatchingOpponent(true);
                  // Auto-scroll to video player after a short delay
                  setTimeout(() => {
                    document.getElementById('opponent-video-player')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 100);
                }}
                variant="outline"
                size="sm"
                className={isWatchingRed ? 'border-red-500/50 text-red-400 hover:bg-red-500/20' : 'border-blue-500/50 text-blue-400 hover:bg-blue-500/20'}
              >
                <Play className="w-4 h-4 mr-2" />
                Watch Their Response First
              </Button>
            </div>
          )}
          
          {/* Video Player for watching opponent */}
          {watchingOpponent && videoToWatch && (
            <div id="opponent-video-player" className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-medium ${isWatchingRed ? 'text-red-400' : 'text-blue-400'}`}>
                  {isWatchingRed ? "🔴" : "🔵"} {opponentName}&apos;s Response
                </span>
                <Button 
                  onClick={() => setWatchingOpponent(false)}
                  variant="ghost"
                  size="sm"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              {/* Video container - maintains 16:9 aspect ratio, video contained within */}
              {/* CSS ensures video displays upright regardless of recording orientation metadata */}
              <div className="relative w-full bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '16/9' }}>
                <video
                  src={videoToWatch}
                  controls
                  playsInline
                  className="absolute inset-0 w-full h-full object-contain bg-black video-playback-fix"
                  style={{ 
                    objectFit: 'contain',
                    /* Ensure video respects its natural orientation */
                    imageOrientation: 'from-image'
                  }}
                  onError={(e) => {
                    // Video not ready yet - show retry message
                    const parent = e.target.parentElement;
                    if (parent && !parent.querySelector('.video-loading-msg')) {
                      const msg = document.createElement('div');
                      msg.className = 'video-loading-msg absolute inset-0 flex items-center justify-center bg-black/80 rounded-lg';
                      msg.innerHTML = '<div class="text-center"><p class="text-yellow-400 text-sm mb-2">⏳ Video processing...</p><p class="text-xs text-muted-foreground">Refresh in a few seconds</p></div>';
                      parent.appendChild(msg);
                    }
                  }}
                  onCanPlay={(e) => {
                    // Video ready - remove loading message
                    const parent = e.target.parentElement;
                    const msg = parent?.querySelector('.video-loading-msg');
                    if (msg) msg.remove();
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Watch their argument, then record your response!
              </p>
            </div>
          )}
          
          {!watchingOpponent && (
            <>
              <p className="text-sm text-muted-foreground mb-3">
                {userSide === "red" ? "You go first!" : `${debate.red_corner_username || "Red"} finished — your turn!`}
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                Ready for {roundInfo?.display}? You have {Math.floor(maxTime / 60)} minutes.
              </p>
            </>
          )}
          
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button 
              onClick={onStartRound}
              disabled={startingRound}
              className="bg-gradient-to-r from-primary to-secondary"
              size="lg"
            >
              {startingRound ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
              ) : isVoice ? (
                <Mic className="w-4 h-4 mr-2" />
              ) : (
                <Video className="w-4 h-4 mr-2" />
              )}
              Start Recording
            </Button>
            {/* Focus Mode Toggle - thumb reachable */}
            {onFocusModeToggle && (
              <Button 
                onClick={onFocusModeToggle}
                variant="outline"
                size="lg"
                className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
              >
                <Maximize2 className="w-4 h-4 mr-2" />
                Focus Mode
              </Button>
            )}
          </div>
          {/* De-emphasized Concede */}
          <div className="mt-3 text-center">
            <button 
              onClick={onConcede}
              className="text-xs text-muted-foreground/60 hover:text-red-400/80 transition-colors"
            >
              Concede Debate
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Both completed this round
  if (bothCompleted) {
    return (
      <div className="bg-card/30 rounded-lg border border-white/10 p-4">
        <div className="text-center py-2">
          <div className="flex items-center justify-center gap-2 text-green-500 mb-2">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">{roundInfo?.name} Complete!</span>
          </div>
          {currentRound < 3 ? (
            <p className="text-sm text-muted-foreground">
              Moving to next round...
            </p>
          ) : (
            <p className="text-sm text-green-400">
              🎉 All rounds complete! Debate finished.
            </p>
          )}
        </div>
      </div>
    );
  }
  
  // Default: show current status
  return (
    <div className="bg-card/30 rounded-lg border border-white/10 p-4">
      <div className="text-center text-muted-foreground text-sm">
        Round {currentRound}: {myStatus}
      </div>
    </div>
  );
};

// Comments Section
const CommentsSection = ({ debateId, token }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [sending, setSending] = useState(false);

  const fetchComments = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/comments/${debateId}`);
      setComments(res.data);
    } catch (e) {
      console.error("Failed to fetch comments");
    }
  }, [debateId]);

  useEffect(() => {
    fetchComments();
    const interval = setInterval(fetchComments, 5000);
    return () => clearInterval(interval);
  }, [fetchComments]);

  const submitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !token) return;
    setSending(true);
    try {
      await axios.post(`${API}/comments`, { debate_id: debateId, content: newComment }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewComment("");
      fetchComments();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post comment");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-card/30 rounded-lg border border-white/10 overflow-hidden">
      <div className="p-3 border-b border-white/10 flex items-center justify-between">
        <h3 className="font-bold flex items-center gap-2 text-sm">
          <MessageSquare className="w-4 h-4" />
          Comments
        </h3>
        <span className="text-xs text-muted-foreground">{comments.length}</span>
      </div>
      
      <div className="max-h-48 md:max-h-64 overflow-y-auto p-3 space-y-3">
        {comments.length === 0 ? (
          <p className="text-center text-muted-foreground text-sm py-4">No comments yet</p>
        ) : (
          comments.map(comment => (
            <div key={comment.comment_id} className="flex gap-2">
              <Avatar className="w-6 h-6 shrink-0">
                <AvatarFallback className="text-xs">{comment.username[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm truncate">{comment.username}</span>
                  <UserBadge badge={comment.user_badge} />
                </div>
                <p className="text-sm text-muted-foreground break-words">{comment.content}</p>
              </div>
            </div>
          ))
        )}
      </div>

      {token ? (
        <form onSubmit={submitComment} className="p-3 border-t border-white/10 flex gap-2">
          <Input
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            className="flex-1 h-9 text-sm bg-zinc-900/50 border-white/10"
          />
          <Button type="submit" size="sm" disabled={sending || !newComment.trim()}>
            <Send className="w-3 h-3" />
          </Button>
        </form>
      ) : (
        <p className="p-3 text-center text-xs text-muted-foreground border-t border-white/10">
          Log in to comment
        </p>
      )}
    </div>
  );
};

// Highlight Marker (Pro only)
const HighlightMarker = ({ debateId, token, isPro, isRecording }) => {
  const [startTime, setStartTime] = useState(null);
  const [marking, setMarking] = useState(false);

  const startMark = () => {
    if (!isPro) {
      toast.error("Highlights are for Pro Debaters only");
      return;
    }
    setStartTime(Date.now());
    setMarking(true);
    toast.info("Highlight started! Click again to save.");
  };

  const endMark = async () => {
    if (!startTime) return;
    const endTime = Date.now();
    const duration = Math.floor((endTime - startTime) / 1000);
    
    try {
      await axios.post(`${API}/highlights`, {
        debate_id: debateId,
        start_time: 0,
        end_time: duration,
        title: `Highlight ${new Date().toLocaleTimeString()}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Highlight saved to your profile!");
    } catch (e) {
      toast.error("Failed to save highlight");
    } finally {
      setMarking(false);
      setStartTime(null);
    }
  };

  if (!isPro) return null;

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={marking ? endMark : startMark}
      disabled={!isRecording && !marking}
      className={marking ? "border-amber-500 text-amber-500 animate-pulse" : ""}
    >
      <Bookmark className="w-4 h-4 mr-1" />
      {marking ? "Save" : "Mark"}
    </Button>
  );
};

// Split Screen Debate Stage with integrated DebateRecorder
const SplitScreenStage = ({ 
  debate, 
  currentRound, 
  isParticipant, 
  userSide,
  isRecording,
  token,
  rounds,
  recorderRef,
  onRecordingStart,
  onRecordingFinish,
  onDonePressed,
  immersiveMode = false
}) => {
  const roundInfo = ROUND_NAMES[currentRound] || ROUND_NAMES[1];
  const currentRoundData = rounds?.[currentRound - 1];
  const debateType = debate.debate_type || "video";
  const maxTime = debate.max_round_time || 180;

  // Determine turn status for display
  const redStatus = currentRoundData?.red_status || "pending";
  const blueStatus = currentRoundData?.blue_status || "waiting";

  const renderCornerContent = (side) => {
    const isUserSide = userSide === side;
    const cornerUsername = side === 'red' ? debate.red_corner_username : debate.blue_corner_username;
    const cornerPicture = side === 'red' ? debate.red_corner_picture : debate.blue_corner_picture;
    const cornerBadge = side === 'red' ? debate.red_corner_badge : debate.blue_corner_badge;
    const cornerId = side === 'red' ? debate.red_corner_id : debate.blue_corner_id;
    const cornerStatus = side === 'red' ? redStatus : blueStatus;
    const borderColor = side === 'red' ? 'border-[#e11d48]' : 'border-[#3b82f6]';
    const bgColor = side === 'red' ? 'bg-[#e11d48]' : 'bg-[#3b82f6]';
    const textColor = side === 'red' ? 'text-[#e11d48]' : 'text-[#3b82f6]';
    const shadowColor = side === 'red' ? 'shadow-[#e11d48]/30' : 'shadow-[#3b82f6]/30';

    // Show recorder for participant on their side when it's their turn or recording
    if (isParticipant && isUserSide && debate.status === "live") {
      return (
        <DebateRecorder 
          ref={side === userSide ? recorderRef : null}
          debateId={debate.debate_id}
          roundNumber={currentRound}
          token={token}
          debateType={debateType}
          side={side}
          maxTime={maxTime}
          onStartRecording={onRecordingStart}
          onFinishRecording={onRecordingFinish}
          onDonePressed={onDonePressed}
          turnStatus={cornerStatus}
        />
      );
    }

    // Default display for non-participant or opponent view
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <div className={`w-24 h-24 md:w-32 md:h-32 rounded-full border-4 ${borderColor} overflow-hidden shadow-lg ${shadowColor} mb-3 relative`}>
          {cornerPicture ? (
            <img src={cornerPicture} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className={`w-full h-full ${bgColor}/20 flex items-center justify-center`}>
              {cornerId ? (
                debateType === "voice" ? (
                  <Mic className={`w-8 h-8 ${textColor}`} />
                ) : (
                  <Video className={`w-8 h-8 ${textColor}`} />
                )
              ) : (
                <Users className={`w-8 h-8 ${textColor}/50`} />
              )}
            </div>
          )}
          {/* Status indicator */}
          {cornerStatus === "recording" && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <div className="w-4 h-4 bg-red-500 rounded-full animate-pulse" />
            </div>
          )}
          {cornerStatus === "completed" && (
            <div className="absolute bottom-0 right-0 bg-green-500 rounded-full p-1">
              <CheckCircle className="w-4 h-4 text-white" />
            </div>
          )}
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center gap-2">
            <h3 className="font-bold">{cornerUsername || (cornerId ? "Debater" : "Awaiting challenger...")}</h3>
            <UserBadge badge={cornerBadge} />
          </div>
          <p className={`text-xs ${textColor} uppercase tracking-wider font-bold`}>
            {side === 'red' ? 'Red Corner' : 'Blue Corner'}
          </p>
          {/* Turn status display */}
          {debate.status === "live" && cornerStatus && (
            <p className="text-[10px] text-muted-foreground mt-1 capitalize">
              {cornerStatus === "recording" ? "🎙 Recording..." : 
               cornerStatus === "completed" ? "✅ Done" : 
               cornerStatus === "waiting" ? "⏳ Waiting" : "📍 Ready"}
            </p>
          )}
        </div>
      </div>
    );
  };

  // In immersive mode, show only the user's recording side with full coverage
  if (immersiveMode && isParticipant && userSide) {
    return (
      <div className="relative w-full h-full bg-black">
        {/* Full-screen recording view for user's side only */}
        <div className="absolute inset-0">
          <DebateRecorder 
            ref={recorderRef}
            debateId={debate.debate_id}
            roundNumber={currentRound}
            token={token}
            debateType={debateType}
            side={userSide}
            maxTime={maxTime}
            onStartRecording={onRecordingStart}
            onFinishRecording={onRecordingFinish}
            onDonePressed={onDonePressed}
            turnStatus={userSide === 'red' ? redStatus : blueStatus}
            immersiveMode={true}
          />
        </div>
        
        {/* Corner indicator */}
        <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-bold z-30 ${
          userSide === 'red' ? 'bg-red-500/80 text-white' : 'bg-blue-500/80 text-white'
        }`}>
          {userSide === 'red' ? '🔴 Red Corner' : '🔵 Blue Corner'}
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-black rounded-lg border border-white/10 overflow-hidden">
      {/* Current Round Display - Hide during recording to reduce clutter */}
      {!isRecording && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 bg-black/80 backdrop-blur px-3 py-1.5 rounded-full">
          <span className="font-bold text-sm">{roundInfo.display}</span>
        </div>
      )}

      {/* Split Screen Layout - 16:9 aspect ratio for YouTube streaming compatibility */}
      {/* On mobile/tablet when recording, show only the recording corner full-width for better preview */}
      <div className={`flex flex-col lg:flex-row aspect-video min-h-[320px] lg:min-h-[450px] ${
        isRecording ? 'recording-active' : ''
      }`}>
        {/* Red Corner - Left Side */}
        {/* Hide on mobile/tablet when blue is recording to give blue full space */}
        {/* When recording alone, this takes full width */}
        <div className={`relative border-b lg:border-b-0 lg:border-r border-white/10 bg-gradient-to-br from-[#e11d48]/10 to-transparent p-2 lg:p-4 transition-all duration-300 ${
          isRecording && userSide === 'blue' ? 'hidden lg:flex lg:flex-1' : 'flex-1'
        } ${isRecording && userSide === 'red' ? 'w-full' : ''}`}>
          {renderCornerContent('red')}
          
          {/* Vote count */}
          <div className="absolute bottom-2 left-2 lg:bottom-3 lg:left-3 bg-[#e11d48]/80 text-white px-2 py-0.5 rounded-full font-bold text-xs lg:text-sm z-20">
            {debate.red_votes} votes
          </div>
        </div>

        {/* VS Divider - Hide during recording */}
        <div className={`hidden lg:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 ${
          isRecording ? 'lg:hidden' : ''
        }`}>
          <div className="w-12 h-12 rounded-full bg-background border-2 border-white/20 flex items-center justify-center">
            <Swords className="w-6 h-6 text-muted-foreground" />
          </div>
        </div>

        {/* Blue Corner - Right Side */}
        {/* Hide on mobile/tablet when red is recording to give red full space */}
        {/* When recording alone, this takes full width and centers content */}
        <div className={`relative bg-gradient-to-bl from-[#3b82f6]/10 to-transparent p-2 lg:p-4 transition-all duration-300 ${
          isRecording && userSide === 'red' ? 'hidden lg:flex lg:flex-1' : 'flex-1'
        } ${isRecording && userSide === 'blue' ? 'w-full' : ''}`}>
          {renderCornerContent('blue')}

          {/* Vote count */}
          <div className="absolute bottom-2 right-2 lg:bottom-3 lg:right-3 bg-[#3b82f6]/80 text-white px-2 py-0.5 rounded-full font-bold text-xs lg:text-sm z-20">
            {debate.blue_votes} votes
          </div>
        </div>
      </div>

      {/* Debate type indicator / Format Badge - Move to bottom right, hide during recording */}
      {!isRecording && (
        <div className={`absolute bottom-12 right-3 flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium z-20 ${
          debateType === "voice" 
            ? "bg-purple-500/80 text-white" 
            : "bg-blue-500/80 text-white"
        }`}>
          {debateType === "voice" ? (
            <><Mic className="w-3 h-3" /> Voice</>
          ) : (
            <><Video className="w-3 h-3" /> Video</>
          )}
        </div>
      )}

      {/* Live indicator - Keep at top left */}
      {debate.status === "live" && (
        <div className="absolute top-3 left-3 flex items-center gap-2 bg-red-500/90 text-white px-2 py-1 rounded-full text-xs font-bold z-20">
          <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
          LIVE
        </div>
      )}
    </div>
  );
};

// Main Debate Room Component
const DebateRoom = () => {
  const { debateId } = useParams();
  const { user, token, canDebate, canVote } = useAuth();
  const { setIsRecording: setGlobalRecording } = useRecording();
  const navigate = useNavigate();
  
  const [debate, setDebate] = useState(null);
  const [myVote, setMyVote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRecording, setIsRecordingLocal] = useState(false);
  const [startingRound, setStartingRound] = useState(false);
  const [selectedTestSide, setSelectedTestSide] = useState('red');  // For test debates
  
  // Landscape mode detection for fullscreen recording/playback
  // Initialize with current orientation state immediately
  const getInitialOrientation = () => {
    if (typeof window !== 'undefined') {
      const isLandscape = window.innerWidth > window.innerHeight;
      const isMobileDevice = window.innerWidth < 1024;
      return isLandscape && isMobileDevice;
    }
    return false;
  };
  
  const [isLandscapeMobile, setIsLandscapeMobile] = useState(getInitialOrientation);
  const [focusMode, setFocusMode] = useState(false); // Manual focus mode toggle
  const [showConcedeOption, setShowConcedeOption] = useState(false); // For concede menu
  const [focusModeUploading, setFocusModeUploading] = useState(false); // Upload in progress state
  const [focusModeTimeLeft, setFocusModeTimeLeft] = useState(60); // Timer display for Focus Mode header
  
  // Update timer display during recording (polls recorder ref)
  // This runs continuously when recording to sync Focus Mode timer with recorder
  useEffect(() => {
    if (!isRecording) {
      // Reset timer display when not recording
      setFocusModeTimeLeft(60);
      return;
    }
    
    const updateTimer = () => {
      // Try to get time from recorder ref
      if (recorderRef.current?.getTimeLeft) {
        const timeLeft = recorderRef.current.getTimeLeft();
        if (typeof timeLeft === 'number' && timeLeft >= 0) {
          setFocusModeTimeLeft(timeLeft);
        }
      }
    };
    
    // Update immediately and then every 100ms for smoother display
    updateTimer();
    const interval = setInterval(updateTimer, 100);
    
    return () => clearInterval(interval);
  }, [isRecording]);
  
  // Force timer sync when entering Focus Mode during recording
  useEffect(() => {
    if ((isLandscapeMobile || focusMode) && isRecording) {
      // Immediate sync when Focus Mode activates
      const syncTimer = () => {
        if (recorderRef.current?.getTimeLeft) {
          const timeLeft = recorderRef.current.getTimeLeft();
          if (typeof timeLeft === 'number' && timeLeft >= 0) {
            console.log('[FocusMode] Timer synced on mode change:', timeLeft);
            setFocusModeTimeLeft(timeLeft);
          }
        }
      };
      
      // Try immediately and after a short delay (for React render cycle)
      syncTimer();
      const timeout = setTimeout(syncTimer, 100);
      const timeout2 = setTimeout(syncTimer, 300);
      
      return () => {
        clearTimeout(timeout);
        clearTimeout(timeout2);
      };
    }
  }, [isLandscapeMobile, focusMode, isRecording]);
  
  // Auto-detect landscape on mobile
  useEffect(() => {
    const checkOrientation = () => {
      const isLandscape = window.innerWidth > window.innerHeight;
      const isMobileDevice = window.innerWidth < 1024;
      setIsLandscapeMobile(isLandscape && isMobileDevice);
    };
    
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);
  
  // Determine if we should show immersive mode (landscape mobile OR manual focus mode)
  const isImmersiveMode = (isLandscapeMobile || focusMode) && debate?.status === "live";
  
  // State to force re-render after viewport changes (iOS standalone fix)
  const [viewportKey, setViewportKey] = useState(0);
  
  // iOS Standalone viewport fix: Re-measure layout after Focus Mode enter and orientation changes
  useEffect(() => {
    if (!isImmersiveMode) return;
    
    let timeoutId;
    
    const handleViewportChange = () => {
      // Clear any pending timeout
      if (timeoutId) clearTimeout(timeoutId);
      
      // Re-measure after a short delay to let iOS settle
      timeoutId = setTimeout(() => {
        console.log('[Viewport] Recalculating layout after viewport change');
        // Force a re-render by updating the viewport key
        setViewportKey(prev => prev + 1);
        
        // Also scroll to ensure proper positioning
        window.scrollTo(0, 0);
      }, 200);
    };
    
    // Initial measurement after Focus Mode enter
    handleViewportChange();
    
    // Listen for viewport changes
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);
    
    // Also handle visibility change (when returning to app)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleViewportChange();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isImmersiveMode]);
  
  // Wrapper to update both local and global recording state - MUST be before handleImmersiveDone
  const setIsRecording = useCallback((value) => {
    setIsRecordingLocal(value);
    setGlobalRecording(value);
  }, [setGlobalRecording]);
  
  // Handle Done button press in immersive mode - triggers the recorder's done action and exits Focus Mode
  const handleImmersiveDone = useCallback(async () => {
    console.log('[FocusMode] Done pressed, starting upload...');
    
    // Set uploading state immediately
    setFocusModeUploading(true);
    setIsRecording(false); // Stop showing "Recording" badge, freeze timer
    
    if (recorderRef.current && recorderRef.current.handleDone) {
      const success = await recorderRef.current.handleDone();
      console.log('[FocusMode] Upload result:', success);
      
      // Clear uploading state and exit Focus Mode
      setFocusModeUploading(false);
      setFocusMode(false);
    } else {
      setFocusModeUploading(false);
    }
  }, [setFocusMode, setIsRecording]);
  
  // Ref to control the DebateRecorder
  const recorderRef = useRef(null);

  const isPro = user?.subscription_tier === "pro" || user?.role === "admin";

  const fetchDebate = useCallback(async () => {
    try {
      const debateRes = await axios.get(`${API}/debates/${debateId}`);
      setDebate(debateRes.data);
      
      if (token) {
        try {
          const voteRes = await axios.get(`${API}/votes/${debateId}/my-vote`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setMyVote(voteRes.data.side);
        } catch {
          // User hasn't voted yet
        }
      }
    } catch (e) {
      toast.error("Debate not found");
      navigate("/browse");
    } finally {
      setLoading(false);
    }
  }, [debateId, token, navigate]);

  useEffect(() => {
    fetchDebate();
    const interval = setInterval(fetchDebate, 5000);
    return () => clearInterval(interval);
  }, [fetchDebate]);

  const [showSideSelect, setShowSideSelect] = useState(false);

  const handleJoin = async (selectedSide = null) => {
    if (!user) {
      toast.error("Please log in to join the debate");
      navigate("/login");
      return;
    }
    
    try {
      await axios.post(`${API}/debates/${debateId}/join`, 
        { side: selectedSide },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`You joined as ${selectedSide || 'a debater'}!`);
      setShowSideSelect(false);
      fetchDebate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to join");
    }
  };

  const handleVote = async (side) => {
    if (!user) {
      toast.error("Please log in to vote");
      navigate("/login");
      return;
    }
    
    try {
      await axios.post(`${API}/votes`, { debate_id: debateId, side }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyVote(side);
      fetchDebate();
      toast.success(`Voted for ${side} corner!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to vote");
    }
  };

  const handleStartRound = async () => {
    if (!token || !userSide) return;
    
    setStartingRound(true);
    try {
      // For test debates, pass the selected side
      const payload = debate.is_test_debate ? { side: userSide } : {};
      
      // FIRST: Try to start the recorder (camera/mic)
      let recorderStarted = false;
      if (recorderRef.current) {
        recorderStarted = await recorderRef.current.startRecording();
        if (!recorderStarted) {
          toast.error("Failed to start recording. Please check camera/microphone permissions.");
          setStartingRound(false);
          return; // Don't notify backend if recorder failed
        }
      }
      
      // THEN: Notify backend that we're starting (only if recorder worked)
      const res = await axios.post(`${API}/debates/${debateId}/round/start`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(res.data.message || "Recording started!");
      setIsRecording(true);
      fetchDebate();
    } catch (e) {
      // If backend call failed, stop the recorder
      if (recorderRef.current) {
        try {
          recorderRef.current.stopRecording?.();
        } catch (stopErr) {
          console.error("Error stopping recorder after backend failure:", stopErr);
        }
      }
      
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409) {
        toast.error(detail || "Already recording or completed");
      } else if (e.response?.status === 400 && detail?.includes("wait for Red")) {
        toast.info("⏳ " + detail);
      } else {
        toast.error(detail || "Failed to start recording");
      }
    } finally {
      setStartingRound(false);
    }
  };

  const handleFinishRound = async (timeUsed = null) => {
    if (!token || !userSide) return;
    
    try {
      // For test debates, pass the selected side
      const payload = { time_used: timeUsed };
      if (debate.is_test_debate) {
        payload.side = userSide;
      }
      const res = await axios.post(`${API}/debates/${debateId}/round/finish`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || "Turn finished!");
      setIsRecording(false);
      
      // Show opponent notification if Red just finished
      if (res.data.opponent_notification) {
        // This would show for Blue corner
        toast.info(`🎯 ${res.data.opponent_notification}`);
      }
      
      // Show round advancement notification
      if (res.data.round_advanced) {
        toast.success(`✅ Round complete! Moving to ${res.data.round_name}`);
      } else if (res.data.both_completed && res.data.current_round === 3) {
        toast.success("🎉 Debate complete! All rounds finished.");
      }
      
      fetchDebate();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409) {
        // Already finished - ignore duplicate calls
        console.log("Round already finished");
      } else {
        toast.error(detail || "Failed to finish turn");
      }
    }
  };

  const handleTimeUp = useCallback(async () => {
    // Only fire once per round
    if (!isRecording) return;
    setIsRecording(false);
    toast.warning("⏰ Time's up! Recording stopped.");
    
    // Stop the recorder and upload
    if (recorderRef?.current) {
      try {
        await recorderRef.current.finishRecording();
      } catch (e) {
        console.error("Error stopping recording:", e);
      }
    }
    
    // Call finish endpoint
    if (token && debate) {
      // For test debates, use selectedTestSide; for normal debates, determine from corner
      const isTestDebate = debate.is_test_debate && debate.red_corner_id === user?.user_id && debate.blue_corner_id === user?.user_id;
      const side = isTestDebate ? selectedTestSide :
                   debate.red_corner_id === user?.user_id ? 'red' : 
                   debate.blue_corner_id === user?.user_id ? 'blue' : null;
      if (side) {
        try {
          // Pass side parameter for test debates
          const payload = isTestDebate ? { side } : {};
          await axios.post(`${API}/debates/${debateId}/round/finish`, payload, {
            headers: { Authorization: `Bearer ${token}` }
          });
          fetchDebate();
        } catch (e) {
          console.error("Error finishing round:", e);
          fetchDebate(); // Still refresh to get latest state
        }
      }
    }
  }, [isRecording, token, debate, user?.user_id, debateId, fetchDebate, recorderRef, selectedTestSide]);

  const handleTimeWarning = useCallback(() => {
    toast.warning("⚠️ 30 seconds remaining!", { duration: 5000 });
  }, []);

  const handleConcede = async () => {
    if (!token) return;
    
    const confirmed = window.confirm("Are you sure you want to concede? This will end the debate and give your opponent the win.");
    if (!confirmed) return;
    
    try {
      const res = await axios.post(`${API}/debates/${debateId}/concede`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || "You have conceded the debate.");
      fetchDebate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to concede");
    }
  };

  const handleCancelDebate = async () => {
    if (!token) return;
    
    const confirmed = window.confirm("Are you sure you want to cancel this debate? This will delete it and no penalty will be applied.");
    if (!confirmed) return;
    
    try {
      const res = await axios.post(`${API}/debates/${debateId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || "Debate cancelled.");
      navigate("/browse");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel debate");
    }
  };

  const handleWithdrawFromDebate = async () => {
    if (!token) return;
    
    const confirmed = window.confirm("Are you sure you want to withdraw from this debate? The debate will remain open for others to join.");
    if (!confirmed) return;
    
    try {
      const res = await axios.post(`${API}/debates/${debateId}/withdraw`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || "You have withdrawn from the debate.");
      navigate("/browse");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to withdraw from debate");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center relative">
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-[10%] -left-[10%] w-[60%] h-[50%] rounded-full bg-red-600/20 blur-[100px]" />
          <div className="absolute -bottom-[10%] -right-[10%] w-[60%] h-[50%] rounded-full bg-blue-600/25 blur-[100px]" />
        </div>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!debate) return null;

  const isParticipant = user && (debate.red_corner_id === user.user_id || debate.blue_corner_id === user.user_id);
  const isHost = user && debate.creator_id === user.user_id;
  const isAdmin = user?.role === "admin";
  const canControlRounds = isHost || isAdmin;
  
  // Check if this is a test debate (admin controls both sides)
  const isTestDebate = debate.is_test_debate && debate.red_corner_id === user?.user_id && debate.blue_corner_id === user?.user_id;
  
  // For test debates, allow side selection. For normal debates, determine based on which corner they're in.
  const userSide = isTestDebate ? selectedTestSide : 
    debate.red_corner_id === user?.user_id ? 'red' : 
    debate.blue_corner_id === user?.user_id ? 'blue' : null;
  
  // Check if user can join - either as blue corner (regular) or in arena debates
  const isArenaWithBothOpen = debate.is_arena && !debate.red_corner_id && !debate.blue_corner_id;
  const canJoinAsBlue = debate.status === "open" && !debate.blue_corner_id && debate.red_corner_id && debate.red_corner_id !== user?.user_id;
  const canJoin = (isArenaWithBothOpen || canJoinAsBlue) && user && canDebate;
  
  const currentRound = debate.current_round || 1;
  const rounds = debate.rounds || [];
  const currentRoundData = rounds[currentRound - 1];
  const roundState = currentRoundData?.state || ROUND_STATE.PENDING;
  const roundInfo = ROUND_NAMES[currentRound];

  return (
    <div className={`min-h-screen bg-[#0a0a0f] relative ${isImmersiveMode ? 'pb-0' : 'pb-24'}`}>
      {/* Immersive Mode (Focus Mode / Landscape) - Full Height Container for iOS Standalone */}
      {/* This creates a full-screen overlay with header/footer, and the main content area 
          will be repositioned via CSS transform to fill the center */}
      {isImmersiveMode && (
        <div 
          key={`immersive-${viewportKey}`}
          className="fixed inset-0 z-[9999] bg-black flex flex-col pointer-events-auto"
          style={{
            height: '100dvh',
            minHeight: '-webkit-fill-available',
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingLeft: 'env(safe-area-inset-left, 0px)',
            paddingRight: 'env(safe-area-inset-right, 0px)',
          }}
        >
          {/* Header - Fixed height with timer */}
          <div className="shrink-0 flex items-center justify-between px-3 py-2 bg-black border-b border-white/10">
            <div className="flex items-center gap-2">
              <Badge variant="destructive" className="text-xs px-2 py-0.5">
                <span className="w-1.5 h-1.5 bg-white rounded-full mr-1.5 animate-pulse"></span>
                LIVE
              </Badge>
              <span className={`text-xs font-bold ${userSide === 'red' ? 'text-red-400' : 'text-blue-400'}`}>
                {roundInfo?.display || `Round ${currentRound}`}
              </span>
            </div>
            
            {/* Timer display - shows during recording */}
            {isRecording && (
              <div className="flex items-center gap-2">
                <div className={`px-3 py-1 rounded-full font-mono text-sm font-bold ${
                  focusModeTimeLeft <= 10 
                    ? 'bg-red-600 text-white animate-pulse' 
                    : 'bg-white/10 text-white'
                }`}>
                  ⏱️ {Math.floor(focusModeTimeLeft / 60)}:{String(focusModeTimeLeft % 60).padStart(2, '0')}
                </div>
              </div>
            )}
            
            {!isRecording && (
              <span className="text-xs font-medium truncate max-w-[180px] text-white/80">{debate.title}</span>
            )}
          </div>

          {/* Main Recording Area - Renders the same SplitScreenStage, preserving camera stream */}
          <div className="flex-1 relative overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-center p-2">
              <div 
                className={`relative w-full h-full rounded-lg overflow-hidden border-2 ${
                  userSide === 'red' ? 'border-red-500/50' : 'border-blue-500/50'
                } bg-black`}
                style={{ maxHeight: '100%' }}
              >
                {/* SplitScreenStage with immersiveMode renders single-side view */}
                <SplitScreenStage 
                  debate={debate} 
                  currentRound={currentRound}
                  isParticipant={isParticipant}
                  userSide={userSide}
                  isRecording={isRecording}
                  token={token}
                  rounds={rounds}
                  recorderRef={recorderRef}
                  onRecordingStart={() => setIsRecording(true)}
                  onRecordingFinish={() => setIsRecording(false)}
                  onDonePressed={() => handleFinishRound()}
                  immersiveMode={true}
                />
                
                {/* Uploading Overlay - shown when upload is in progress */}
                {focusModeUploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-50">
                    <div className="text-center p-6">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500 mx-auto mb-4" />
                      <p className="text-lg text-white font-bold mb-2">Uploading your response...</p>
                      <p className="text-sm text-muted-foreground">Please wait, don&apos;t leave this screen</p>
                      <div className="mt-4 p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-lg">
                        <p className="text-xs text-yellow-400">⏳ This may take a moment for longer recordings</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer - Fixed height, with safe area bottom padding */}
          <div 
            className="shrink-0 bg-black border-t border-white/10"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          >
            {/* Main action buttons */}
            <div className="flex items-center justify-between px-4 py-3">
              {/* Left: Record/Status */}
              <div className="flex-1 flex justify-start">
                {!isRecording && currentRoundData?.[`${userSide}_status`] === "pending" && (
                  <Button 
                    onClick={handleStartRound}
                    disabled={startingRound}
                    size="lg"
                    className={`${userSide === 'red' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'} text-white font-bold px-6`}
                  >
                    {startingRound ? "Starting..." : "🎬 Record"}
                  </Button>
                )}
                {isRecording && (
                  <Badge className="bg-red-600 text-white animate-pulse px-4 py-2 text-sm">
                    ● Recording
                  </Badge>
                )}
                {focusModeUploading && (
                  <Badge className="bg-green-600 text-white px-4 py-2 text-sm">
                    ⏳ Uploading...
                  </Badge>
                )}
                {currentRoundData?.[`${userSide}_status`] === "completed" && !focusModeUploading && (
                  <span className="text-sm text-green-400 font-medium">✓ Turn Complete</span>
                )}
              </div>

              {/* Center: Done button (only when recording, hidden during upload) */}
              <div className="flex-1 flex justify-center">
                {isRecording && !focusModeUploading && (
                  <Button 
                    onClick={handleImmersiveDone}
                    size="lg"
                    className="bg-green-600 hover:bg-green-700 text-white font-bold px-8"
                  >
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Done
                  </Button>
                )}
              </div>

              {/* Right: Exit Focus - disabled during upload */}
              <div className="flex-1 flex justify-end">
                <Button
                  onClick={() => {
                    if (focusModeUploading) {
                      // Show warning if trying to exit during upload
                      if (window.confirm('Upload in progress! Leaving now may lose your recording. Are you sure?')) {
                        setFocusMode(false);
                        setIsLandscapeMobile(false); // Force exit landscape mode too
                        setFocusModeUploading(false);
                      }
                    } else {
                      setFocusMode(false);
                      setIsLandscapeMobile(false); // Force exit landscape mode too
                    }
                  }}
                  variant="outline"
                  size="lg"
                  className={`border-white/20 text-white ${focusModeUploading ? 'opacity-50' : 'hover:bg-white/10'}`}
                >
                  <X className="w-4 h-4 mr-2" />
                  {focusModeUploading ? 'Please Wait' : 'Exit'}
                </Button>
              </div>
            </div>

            {/* Secondary action: Concede (de-emphasized) - hidden during upload */}
            {!focusModeUploading && (
            <div className="flex justify-center pb-2">
              {!showConcedeOption ? (
                <button 
                  onClick={() => setShowConcedeOption(true)}
                  className="text-xs text-muted-foreground/60 hover:text-red-400/80 transition-colors"
                >
                  More options...
                </button>
              ) : (
                <div className="flex items-center gap-3 animate-in fade-in">
                  <button 
                    onClick={() => {
                      handleConcede();
                      setShowConcedeOption(false);
                    }}
                    className="text-xs text-red-400 hover:text-red-300"
                  >
                    ⚠️ Concede Match
                  </button>
                  <button 
                    onClick={() => setShowConcedeOption(false)}
                    className="text-xs text-muted-foreground hover:text-white"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      )}

      {/* Ambient red/blue glow for debate arena feel */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-[10%] -left-[10%] w-[60%] h-[50%] rounded-full bg-red-600/20 blur-[100px]" />
        <div className="absolute -bottom-[10%] -right-[10%] w-[60%] h-[50%] rounded-full bg-blue-600/25 blur-[100px]" />
      </div>
      
      {/* Hide navbar in immersive mode */}
      {!isImmersiveMode && <Navbar />}
      
      <div className={`w-full mx-auto py-4 md:py-6 debate-room-wrapper ${
        isImmersiveMode 
          ? 'max-w-none pt-14 pb-28 px-2' 
          : 'max-w-7xl px-4 md:px-6'
      }`}>
        {/* Back button - hide in immersive mode */}
        {!isImmersiveMode && (
          <Link 
            to="/browse" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 group text-sm"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back
          </Link>
        )}

        {/* Debate Header - hide in immersive mode */}
        {!isImmersiveMode && (
        <div className="mb-4">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Badge variant={debate.status === "live" ? "destructive" : debate.status === "open" ? "secondary" : "outline"}>
              {debate.status === "live" && <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></span>}
              {debate.status.toUpperCase()}
            </Badge>
            <Badge variant="outline">{debate.category}</Badge>
            {debate.is_ai_generated && <Badge variant="outline" className="bg-purple-500/10 border-purple-500/30">🤖 AI Topic</Badge>}
            {debate.is_test_debate && <Badge variant="outline" className="bg-yellow-500/10 border-yellow-500/30 text-yellow-400">🧪 Test Mode</Badge>}
          </div>
          <h1 className="text-xl md:text-2xl font-bold tracking-tight mb-1">{debate.title}</h1>
          
          {/* Show positions if available */}
          {(debate.red_position || debate.blue_position) ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-3">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                  <span className="text-xs font-bold text-red-400">RED ARGUES</span>
                </div>
                <p className="text-sm">{debate.red_position}</p>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                  <span className="text-xs font-bold text-blue-400">BLUE ARGUES</span>
                </div>
                <p className="text-sm">{debate.blue_position}</p>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm line-clamp-2">{debate.description}</p>
          )}
        </div>
        )}

        {/* Status Banner for Ended Debates */}
        {(debate.status === "ended_no_response" || debate.status === "ended_concession" || debate.status === "completed") && (
          <div className={`mb-4 p-4 rounded-lg border ${
            debate.status === "completed" ? "bg-green-500/10 border-green-500/30" :
            debate.status === "ended_concession" ? "bg-yellow-500/10 border-yellow-500/30" :
            "bg-red-500/10 border-red-500/30"
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 font-bold">
                  {debate.status === "completed" && <CheckCircle className="w-5 h-5 text-green-500" />}
                  {debate.status === "ended_concession" && <AlertTriangle className="w-5 h-5 text-yellow-500" />}
                  {debate.status === "ended_no_response" && <XCircle className="w-5 h-5 text-red-500" />}
                  <span>
                    {debate.status === "completed" && "Debate Completed"}
                    {debate.status === "ended_concession" && "Debate Ended — Concession"}
                    {debate.status === "ended_no_response" && "Debate Ended — No Response"}
                  </span>
                </div>
                {debate.winner_username && (
                  <p className="text-sm text-muted-foreground mt-1">
                    🏆 Winner: <span className="font-medium text-foreground">{debate.winner_username}</span>
                  </p>
                )}
                {/* Spar Ratings Display */}
                {(debate.red_corner_spar_rating || debate.blue_corner_spar_rating) && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-muted-foreground uppercase tracking-wide">Spar Ratings</span>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button className="text-muted-foreground hover:text-foreground">
                                <Info className="w-3.5 h-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-zinc-800 text-white border-white/10">
                              <p className="text-sm">Spar Rating measures performance — presence, energy, clarity, and pacing.</p>
                              <p className="text-sm mt-1">It does not decide the winner. The crowd vote does.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      {debate.red_corner_spar_rating && (
                        <div className="flex items-center gap-2">
                          <span className="text-red-400 text-sm font-medium">🔴 {debate.red_corner_username?.split(' ')[0] || 'Red'}:</span>
                          <span className={`text-lg font-bold ${
                            debate.red_corner_spar_rating >= 80 ? 'text-green-400' :
                            debate.red_corner_spar_rating >= 60 ? 'text-yellow-400' :
                            'text-orange-400'
                          }`}>{debate.red_corner_spar_rating}</span>
                        </div>
                      )}
                      {debate.blue_corner_spar_rating && (
                        <div className="flex items-center gap-2">
                          <span className="text-blue-400 text-sm font-medium">🔵 {debate.blue_corner_username?.split(' ')[0] || 'Blue'}:</span>
                          <span className={`text-lg font-bold ${
                            debate.blue_corner_spar_rating >= 80 ? 'text-green-400' :
                            debate.blue_corner_spar_rating >= 60 ? 'text-yellow-400' :
                            'text-orange-400'
                          }`}>{debate.blue_corner_spar_rating}</span>
                        </div>
                      )}
                    </div>
                    {/* Helper text explaining the difference */}
                    <p className="text-xs text-muted-foreground mt-2 italic">
                      Votes decide who won. Spar Rating reflects how you performed.
                    </p>
                  </div>
                )}
              </div>
              <Badge variant="outline" className={
                debate.status === "completed" ? "text-green-400 border-green-400/30" :
                debate.status === "ended_concession" ? "text-yellow-400 border-yellow-400/30" :
                "text-red-400 border-red-400/30"
              }>
                {debate.end_reason?.replace("_", " ").toUpperCase() || debate.status.toUpperCase()}
              </Badge>
            </div>
          </div>
        )}

        {/* Round Indicator - hide in immersive mode */}
        {debate.status === "live" && !isImmersiveMode && (
          <div className="mb-4">
            <RoundIndicator currentRound={currentRound} rounds={rounds} />
          </div>
        )}

        {/* Test Debate Side Switcher - hide in immersive mode */}
        {isTestDebate && debate.status === "live" && !isImmersiveMode && (
          <div className="mb-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-lg p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-bold text-yellow-400 flex items-center gap-2">
                  🧪 Test Mode - Solo Debate
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  You control both sides. Switch sides to record for each corner.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Recording as:</span>
                <div className="flex rounded-lg overflow-hidden border border-white/20">
                  <button
                    onClick={() => setSelectedTestSide('red')}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      selectedTestSide === 'red' 
                        ? 'bg-red-500 text-white' 
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    🔴 Red
                  </button>
                  <button
                    onClick={() => setSelectedTestSide('blue')}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      selectedTestSide === 'blue' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    🔵 Blue
                  </button>
                </div>
              </div>
            </div>
            {/* Progress indicator */}
            <div className="mt-3 grid grid-cols-6 gap-1">
              {[1, 2, 3].map((r) => {
                const roundData = rounds[r - 1] || {};
                return (
                  <React.Fragment key={r}>
                    <div className={`text-center text-xs py-1 rounded ${
                      roundData.red_status === 'completed' ? 'bg-green-500/20 text-green-400' :
                      roundData.red_status === 'recording' ? 'bg-red-500/20 text-red-400 animate-pulse' :
                      'bg-zinc-800 text-zinc-500'
                    }`}>
                      R{r} Red {roundData.red_status === 'completed' ? '✓' : ''}
                    </div>
                    <div className={`text-center text-xs py-1 rounded ${
                      roundData.blue_status === 'completed' ? 'bg-green-500/20 text-green-400' :
                      roundData.blue_status === 'recording' ? 'bg-blue-500/20 text-blue-400 animate-pulse' :
                      'bg-zinc-800 text-zinc-500'
                    }`}>
                      R{r} Blue {roundData.blue_status === 'completed' ? '✓' : ''}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Main Content - Full Width Stage Layout */}
        <div className="space-y-4">
          {/* Split Screen Stage - Full Width 16:9 */}
          <SplitScreenStage 
            debate={debate} 
            currentRound={currentRound}
            isParticipant={isParticipant}
            userSide={userSide}
            isRecording={isRecording}
            token={token}
            rounds={rounds}
            recorderRef={recorderRef}
            onRecordingStart={() => setIsRecording(true)}
            onRecordingFinish={() => setIsRecording(false)}
            onDonePressed={() => handleFinishRound()}
          />

          {/* Turn-Based Recording Controls */}
          {debate.status === "live" && isParticipant && userSide && (
            <TurnBasedControls 
              debate={debate}
              userSide={userSide}
              currentRound={currentRound}
              currentRoundData={currentRoundData}
              roundInfo={roundInfo}
              isRecording={isRecording}
              setIsRecording={setIsRecording}
              startingRound={startingRound}
              isPro={isPro}
              token={token}
              debateId={debateId}
              onStartRound={handleStartRound}
              onFinishRound={handleFinishRound}
              onTimeUp={handleTimeUp}
              onTimeWarning={handleTimeWarning}
              onConcede={handleConcede}
              recorderRef={recorderRef}
              onFocusModeToggle={() => setFocusMode(true)}
            />
          )}

          {/* Spectator Mode Indicator - shown for non-participants on live debates */}
          {debate.status === "live" && !isParticipant && (
              <div className="bg-card/30 rounded-lg border border-white/10 p-4">
                <div className="flex items-center justify-center gap-3 text-muted-foreground">
                  <div className="flex items-center gap-2 bg-zinc-800/50 px-3 py-1.5 rounded-full">
                    <Users className="w-4 h-4" />
                    <span className="text-sm font-medium">Spectator Mode</span>
                  </div>
                </div>
                <p className="text-center text-xs text-muted-foreground mt-3">
                  You&apos;re watching this debate live. Vote for your favorite debater below!
                </p>
                {!user && (
                  <p className="text-center text-xs text-primary mt-2">
                    <Link to="/login" className="hover:underline">Sign in</Link> to vote and comment
                  </p>
                )}
              </div>
            )}

            {/* Join CTA for open debates */}
            {debate.status === "open" && (!debate.blue_corner_id || isArenaWithBothOpen) && (
              <div className="bg-card/30 rounded-lg border border-white/10 p-6 text-center">
                <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-bold mb-2">
                  {isArenaWithBothOpen ? "Open Arena - Pick Your Side!" : "Waiting for Opponent"}
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {isArenaWithBothOpen ? "Choose which position you want to argue" : "This debate needs a challenger"}
                </p>
                
                {canJoin && isArenaWithBothOpen && !showSideSelect ? (
                  <Button onClick={() => setShowSideSelect(true)} className="bg-gradient-to-r from-primary to-secondary">
                    <Swords className="w-4 h-4 mr-2" />
                    Join This Debate
                  </Button>
                ) : canJoin && isArenaWithBothOpen && showSideSelect ? (
                  <div className="space-y-3">
                    <p className="text-sm font-medium mb-2">Which side do you want to argue?</p>
                    {debate.red_position && (
                      <Button 
                        onClick={() => handleJoin("red")}
                        className="w-full bg-red-600 hover:bg-red-700 text-left justify-start h-auto py-3 px-4"
                      >
                        <div className="w-full overflow-hidden">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 bg-white rounded-full flex-shrink-0"></span>
                            <span className="font-bold">Red Corner</span>
                          </div>
                          <p className="text-xs opacity-90 mt-1 ml-5 whitespace-normal break-words line-clamp-3">{debate.red_position}</p>
                        </div>
                      </Button>
                    )}
                    {debate.blue_position && (
                      <Button 
                        onClick={() => handleJoin("blue")}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-left justify-start h-auto py-3 px-4"
                      >
                        <div className="w-full overflow-hidden">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 bg-white rounded-full flex-shrink-0"></span>
                            <span className="font-bold">Blue Corner</span>
                          </div>
                          <p className="text-xs opacity-90 mt-1 ml-5 whitespace-normal break-words line-clamp-3">{debate.blue_position}</p>
                        </div>
                      </Button>
                    )}
                    {!debate.red_position && !debate.blue_position && (
                      <div className="flex gap-2 justify-center">
                        <Button onClick={() => handleJoin("red")} className="bg-red-600 hover:bg-red-700">
                          🔴 Red Corner
                        </Button>
                        <Button onClick={() => handleJoin("blue")} className="bg-blue-600 hover:bg-blue-700">
                          🔵 Blue Corner
                        </Button>
                      </div>
                    )}
                    <Button onClick={() => setShowSideSelect(false)} variant="ghost" size="sm">
                      Cancel
                    </Button>
                  </div>
                ) : canJoin ? (
                  <Button onClick={() => handleJoin()} className="bg-gradient-to-r from-primary to-secondary">
                    <Swords className="w-4 h-4 mr-2" />
                    Accept Challenge
                  </Button>
                ) : !canDebate && user && (
                  <div className="text-sm text-muted-foreground">
                    <Lock className="w-4 h-4 inline mr-1" />
                    Pro Debater subscription required
                  </div>
                )}
                
                {/* Withdraw button for anyone who joined an open debate (before opponent joins) */}
                {isParticipant && debate?.status === "open" && 
                 !(debate?.red_corner_id && debate?.blue_corner_id) && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <Button 
                      onClick={handleWithdrawFromDebate} 
                      variant="ghost" 
                      size="sm"
                      className="text-muted-foreground hover:text-yellow-400 hover:bg-yellow-500/10"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Leave Debate
                    </Button>
                    <p className="text-xs text-muted-foreground mt-1">Leave your spot - debate stays open for others</p>
                  </div>
                )}
                
                {/* Cancel button for creator/admin - only show if NOT an arena debate or if admin wants full delete */}
                {(isHost || isAdmin) && debate?.status === "open" && (
                  <div className={`mt-4 pt-4 ${debate?.is_arena && isParticipant ? '' : 'border-t border-white/10'}`}>
                    <Button 
                      onClick={handleCancelDebate} 
                      variant="ghost" 
                      size="sm"
                      className="text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      {debate?.is_arena ? "Delete Arena" : "Cancel Debate"}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-1">
                      {debate?.is_arena ? "Permanently delete this arena" : "No penalty - debate hasn't started yet"}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Voting Section */}
            {(debate.status === "live" || debate.status === "completed") && (
              <div className="bg-card/30 rounded-lg border border-white/10 p-4">
                <TugOfWarVoting 
                  redVotes={debate.red_votes}
                  blueVotes={debate.blue_votes}
                  myVote={myVote}
                  onVote={handleVote}
                  disabled={!user || debate.status === "completed"}
                />
                <p className="text-center text-xs text-muted-foreground mt-2">
                  <Vote className="w-3 h-3 inline mr-1" />
                  {debate.total_votes} total votes
                </p>
              </div>
            )}

            {/* Playback Section - Available for completed debates */}
            {debate.status === "completed" && (
              <div className="bg-card/30 rounded-lg border border-white/10 p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold flex items-center gap-2">
                    <Film className="w-4 h-4" />
                    Debate Replay
                  </h3>
                  <Badge variant="outline" className="text-green-400 border-green-400/30">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Complete
                  </Badge>
                </div>
                <DebatePlayback debateId={debateId} />
              </div>
            )}

          {/* Comments and Debate Info - Side by side below the stage */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Comments */}
            <CommentsSection debateId={debateId} token={token} />

            {/* Debate Info */}
            <div className="bg-card/30 rounded-lg border border-white/10 p-4 h-fit">
              <h4 className="text-sm font-bold mb-3">Debate Info</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type</span>
                  <span className="capitalize">{debate.debate_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Round Time</span>
                  <span>{debate.max_round_time < 60 ? `${debate.max_round_time} sec` : `${Math.floor((debate.max_round_time || 300) / 60)} min`}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Created by</span>
                  <Link to={`/profile/${debate.creator_id}`} className="text-primary hover:underline truncate ml-2">
                    {debate.creator_username}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Emote Bar at Bottom - z-50 to stay above all content overlays */}
      {debate.status === "live" && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-xl border-t border-white/10 p-3 pb-[max(12px,env(safe-area-inset-bottom))] shadow-lg shadow-black/20">
          <div className="max-w-7xl mx-auto">
            <EmotePicker debateId={debateId} token={token} isPro={isPro} />
          </div>
        </div>
      )}

      {/* CSS */}
      <style>{`
        @keyframes float-up {
          0% { transform: translateY(0) scale(1); opacity: 1; }
          100% { transform: translateY(-60px) scale(1.3); opacity: 0; }
        }
        .animate-float-up {
          animation: float-up 1.5s ease-out forwards;
        }
        .mirror {
          transform: scaleX(-1);
          /* GPU acceleration for smooth preview */
          will-change: transform;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }
        /* Smooth video playback optimization */
        video.mirror {
          image-rendering: auto;
          -webkit-transform: scaleX(-1) translateZ(0);
          transform: scaleX(-1) translateZ(0);
        }
        /* Video playback orientation fix - ensures videos display upright */
        .video-playback-fix {
          /* Modern browsers respect EXIF orientation */
          image-orientation: from-image;
        }
        /* Ensure all video elements in playback containers render smoothly */
        video[controls] {
          /* Hardware acceleration for smooth playback */
          transform: translateZ(0);
          -webkit-transform: translateZ(0);
        }
      `}</style>
    </div>
  );
};

export { DebateRecorder };
export default DebateRoom;
